import { Component, ElementRef, EventEmitter, HostBinding, HostListener, Injectable, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as d3 from "d3";
import { tree } from "d3";
import moment from 'moment';
import { Subscription } from 'rxjs';
import { DataService } from 'src/app/services/data.service';
import { DynamicCustomTooltipService } from 'src/app/services/dynamic-custom-tooltip.service';
import { FilterService } from 'src/app/services/filter.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { NewFilterService } from 'src/app/services/new-filter.service';

/**
 * Fallback colour pool used (embedded mode only) to colour programs / legend
 * flags / brands that have no explicit colour in the widget config, so nothing
 * renders un-coloured. Colours are picked deterministically by name via
 * paletteColorFor() so a given label keeps the same colour across re-renders.
 */
export const GANTT_RANDOM_COLOR_PALETTE: string[] = [
  "#8A05F3", "#9b59b6", "#1566DE", "#E74028", "#2CB239", "#25D3EB", "#FFB039", "#1566DE", "#7848FF", "#7AD045", "#CB04DC", "#FA195C", "#9E0000",
  "#EACD37", "#DD630B", "#BCD03F", "#14BDA9", "#8A05F3", "#D9476A", "#2DADB6", "#E468C2", "#199CD4", "#0934CC",
  "#e74c3c", "#3498db", "#2ecc71", "#f1c40f", "#9b59b6", "#1abc9c", "#34495e", "#e67e22", "#16a085", "#d35400",
  "#2980b9", "#8e44ad", "#c0392b", "#27ae60", "#f39c12", "#7f8c8d",
];

@Injectable({
  providedIn: 'root'
})
@Component({
    selector: 'app-gantt-chart',
    templateUrl: './gantt-chart.component.html',
    styleUrls: ['./gantt-chart.component.scss'],
    standalone: false
})
export class GanttChartComponent implements OnInit, OnDestroy, OnChanges {
  
  @Input('showGanttTooltip') showGanttTooltip: boolean = false
  @Input('showPendingTooltip') showPendingTooltip: boolean = false
  @Input('showChangeTooltip') showChangeTooltip: boolean = false
  @Input('isFullscreen') isFullscreen: boolean = false;

  @Input('widgets') widgets: any = ""
  /** When true, skip FilterService API subscriptions and render from external inputs. */
  @Input('embeddedMode') embeddedMode: boolean = false;
  /** Pre-loaded transaction rows (Scraii AI / embedded usage). */
  @Input('externalChartData') externalChartData: any[] | null = null;
  /** Pre-loaded program header rows (Scraii AI / embedded usage). */
  @Input('externalProgramData') externalProgramData: any[] | null = null;
  /** Narrow layout for Scraii floating chatbot (embedded mode only). */
  @Input('compactLayout') compactLayout: boolean = false;
  /** Unique DOM id for the chart host (required when multiple embedded instances exist). */
  @Input('chartContainerId') chartContainerId?: string;

  @Output() emitLoader = new EventEmitter();
  @Output() chartLayoutChanged = new EventEmitter<number>();

  @ViewChild('fs') fs!: ElementRef;
  @ViewChild('GanttChartMainContainer', { static: true }) GanttChartContainer!: ElementRef;
  
  @ViewChild('tooltipTemplate') tooltipTemplate!: ElementRef | any;
  @ViewChild('showGanttCustomTooltip') showGanttCustomTooltip!: ElementRef | any;
  @ViewChild('showChangeCustomTooltip') showChangeCustomTooltip!: ElementRef | any;
  @ViewChild('showPendingCustomTooltip') showPendingCustomTooltip!: ElementRef | any;
  @ViewChild('showPointerCustomTooltip') showPointerCustomTooltip!: ElementRef | any;
  
  ganttChartData: any;
  headerChartData: any = { dispense: [], kpi: [], program: [] };
  divId: string = "ganttChart";
  width: number = 0;
  height: number = 0;
  props: any = {};
  /** Preserves manual expand/collapse across plotChart() relayouts in embedded mode. */
  private treeNodeExpandOverrides = new Map<string, 'expanded' | 'collapsed'>();
  private embeddedWidthRetryPending = false;
  private chartViewReady = false;
  private pendingPlotChart = false;
  private plotChartInProgress = false;
  private lastEmbeddedPlotWidth = 0;
  lastPlotReason = 'unknown';
  private embeddedPlotContext: { redraw: () => void; hostEl: HTMLElement } | null = null;
  private lastTreeExpandAt = 0;
  private activeTreeToggle: ((event: any, d: any) => void) | null = null;
  isActive: boolean = false;
  currentVisibleData: any = [];
  collapse: any = "collapse";
  transactionName: any;
  clientKey: any;
  moduleKey: any;
  dashboardKey: any;
  patientId: any;
  transactionData: any;
  refillData: any;
  radarChartData: any;
  pendingInformation: any = [];
  showPointerTooltip: boolean = false;
  pointerTooltipData: any;
  reqSubcription: Subscription[] = [];
  currentLegendSelections: any = [];
  selctedLegendData: any = [];
  cardName: any;
  cardId: any
  totalLegendData: any = []
  unique: any = []
  legendID: any;
  showCircleFlags: any = false
  monthsVisible: any = 6;
  widgetEndpoint:any;

  // @HostListener('fullscreenchange', ['$event'])
  // @HostListener('webkitfullscreenchange', ['$event'])
  // @HostListener('mozfullscreenchange', ['$event'])
  // @HostListener('MSFullscreenChange', ['$event'])
  // screenChange(event: any) {
  //   if (this.isFullscreen == true) {
  //     this.closeFullscreen();
  //     this.isFullscreen = false
  //   }
  // }
  
  // @HostBinding('class.is-fullscreen') isFullscreen = false;


  // closeFullscreen(): void {
  //   this.isFullscreen = false;
  //   this.isActive = false;
  //   if (document.exitFullscreen) {
  //     document.exitFullscreen();
  //   }
  //   setTimeout(() => {
  //     // this.initiateCharts()
  //     // this.plotChart()
  //   }, 100);
  // }

  loader(loader:any) {
    this.emitLoader.emit(loader)
  }

  constructor(
    private dataService: DataService, 
    private filterService: NewFilterService, 
    private router: ActivatedRoute,
    private navigationService: NavigationService,
    private filterServiceOld: FilterService,
    private dynamicCustomTooltipService: DynamicCustomTooltipService,
    private viewContainerRef: ViewContainerRef
  ) {
    this.totalLegendData = []
    this.selctedLegendData = []
    this.currentLegendSelections = []
  }

  // Tooltip Show

  private get embeddedTooltipOptions() {
    return this.embeddedMode ? { scrollReposition: true } : undefined;
  }

  private showTooltip(myType: any, myData: any, myX: any, myY: any, svgWidth: any, event:any): void {
    this.hideCustomTooltip();
    this.pointerTooltipData = ''
    this.transactionData = ""
    this.transactionName = ""
    this.transactionName = myData?.transactiontype
    this.legendID = myData?.legendId

    if (this.transactionName && this.legendID != 'change') {
      if (myData.transactiontype != undefined || myData.transactiontype != null || myData.transactiontype != '' || myData.transactiontype != 'unknown') {
        this.getTransactionPopupDetails(100108, myData.transactiontype, myData.transactionid);
        if (this.dynamicCustomTooltipService) {
          this.dynamicCustomTooltipService?.showTooltip(this.showGanttCustomTooltip,this.viewContainerRef,event, undefined, this.embeddedTooltipOptions);
        }
        // this.showGanttTooltip = true
        // this.showPendingTooltip = false
        // this.showPointerTooltip = false
        // this.showChangeTooltip = false
      }
    } else if (this.transactionName && this.legendID == 'change') {
      if (myData.transactiontype != undefined || myData.transactiontype != null || myData.transactiontype != '' || myData.transactiontype != 'unknown') {
        this.getTransactionPopupDetails(100108, myData.transactiontype, myData.transactionid)
        if (this.dynamicCustomTooltipService) {
          this.dynamicCustomTooltipService?.showTooltip(this.showChangeCustomTooltip,this.viewContainerRef,event, undefined, this.embeddedTooltipOptions);
        }
        // this.showGanttTooltip = false
        // this.showPendingTooltip = false
        // this.showPointerTooltip = false
        // this.showChangeTooltip = true
      }
    } else if (myData?.data?.children != undefined) {
      this.pendingInformation = [{
        heading: myData?.data.name + ' - Information',
        startDate: myData?.data?.startDate,
        endDate: myData?.data?.endDate,
        duration: myData?.data?.duration,
        codes: myData.statuscodes
      }]

      if (this.dynamicCustomTooltipService) {
        this.dynamicCustomTooltipService?.showTooltip(this.showPendingCustomTooltip,this.viewContainerRef,event, undefined, this.embeddedTooltipOptions);
      }
      // this.showPendingTooltip = true
      // this.showGanttTooltip = false
      // this.showPointerTooltip = false
      // this.showChangeTooltip = false
    } else if (myData?.data?.children == undefined) {
      if(myData?.data) {
        if (myData?.data?.transactiontype != undefined || myData?.data?.transactiontype != null || myData?.data?.transactiontype != '' || myData?.data?.transactiontype != 'unknown') {
          this.transactionName = myData.data.name
          this.getTransactionPopupDetails(100108, myData.data.transactiontype, myData.data.transactionid)
          if (this.dynamicCustomTooltipService) {
            this.dynamicCustomTooltipService?.showTooltip(this.showGanttCustomTooltip,this.viewContainerRef,event, undefined, this.embeddedTooltipOptions);
          }
          // this.showGanttTooltip = true
          // this.showPendingTooltip = false
          // this.showPointerTooltip = false
          // this.showChangeTooltip = false
        }
      } else {
        // 
        this.pointerTooltipData = myData
        if(this.pointerTooltipData) {
          this.dynamicCustomTooltipService?.showTooltip(this.showPointerCustomTooltip,this.viewContainerRef,event, true, this.embeddedTooltipOptions);
        }
        // d3.select("#d3Tooltip")
        //   .style('visibility', 'visible')
        //   .style('position', 'absolute')
        //   .style('top', (myY+25) + 'px')
        //   .style('left', (myX-30) + 'px')
        //   .style('z-index', '99999')
        
        // this.showPointerTooltip = true
        // this.showGanttTooltip = false
        // this.showPendingTooltip = false
        // this.showChangeTooltip = false
      }
    }
    
    // let difference = 0
    // this.pointerTooltipData = ''
    // this.transactionData = ""
    // this.transactionName = ""
    // difference = svgWidth - myX
    // this.transactionName = myData?.transactiontype
    // this.legendID = myData?.legendId
    // if (this.isFullscreen == true) {
    //   if (this.transactionName && this.legendID != 'change') {
    //     if (myData.transactiontype != undefined || myData.transactiontype != null || myData.transactiontype != '' || myData.transactiontype != 'unknown') {

    //       this.getTransactionPopupDetails(100108, myData.transactiontype, myData.transactionid)

    //       if (this.headerChartData.kpi.length > 0) {

    //         if (difference > 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 300) + 'px')
    //             .style('left', (myX + 10) + 'px')
    //             .style('right', 'unset')
    //             .style('z-index', '99999')

    //         }
    //         else if (difference < 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 300) + 'px')
    //             .style('right', difference + 'px')
    //             .style('left', 'unset')
    //             .style('z-index', '99999')
    //         }
    //       }
    //       else {
    //         if (difference > 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 150) + 'px')
    //             .style('left', (myX + 10) + 'px')
    //             .style('right', 'unset')
    //             .style('z-index', '99999')

    //         }
    //         else if (difference < 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 150) + 'px')
    //             .style('right', difference + 'px')
    //             .style('left', 'unset')
    //             .style('z-index', '99999')
    //         }

    //       }
    //       document.getElementById('kpi_gantt_popup')?.setAttribute('style', 'visibility:hidden')
    //       document.getElementsByClassName('cdk-overlay-pane custom-tooltip')[0]?.setAttribute('style', 'visibility:hidden')
    //       this.showGanttTooltip = true
    //       this.showPendingTooltip = false
    //       this.showPointerTooltip = false
    //       this.showChangeTooltip = false
    //     }
    //   }
    //   else if (this.transactionName && this.legendID == 'change') {
    //     if (myData.transactiontype != undefined || myData.transactiontype != null || myData.transactiontype != '' || myData.transactiontype != 'unknown') {

    //       this.getTransactionPopupDetails(100108, myData.transactiontype, myData.transactionid)

    //       if (this.headerChartData.kpi.length > 0) {

    //         if (difference > 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 300) + 'px')
    //             .style('left', (myX + 10) + 'px')
    //             .style('right', 'unset')
    //             .style('z-index', '99999')

    //         }
    //         else if (difference < 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 300) + 'px')
    //             .style('right', difference + 'px')
    //             .style('left', 'unset')
    //             .style('z-index', '99999')
    //         }
    //       }
    //       else {
    //         if (difference > 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 150) + 'px')
    //             .style('left', (myX + 10) + 'px')
    //             .style('right', 'unset')
    //             .style('z-index', '99999')

    //         }
    //         else if (difference < 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 150) + 'px')
    //             .style('right', difference + 'px')
    //             .style('left', 'unset')
    //             .style('z-index', '99999')
    //         }

    //       }
    //       document.getElementById('kpi_gantt_popup')?.setAttribute('style', 'visibility:hidden')
    //       document.getElementsByClassName('cdk-overlay-pane custom-tooltip')[0]?.setAttribute('style', 'visibility:hidden')
    //       this.showGanttTooltip = false
    //       this.showPendingTooltip = false
    //       this.showPointerTooltip = false
    //       this.showChangeTooltip = true

    //     }

    //   }
    //   else if (myData?.data?.children != undefined) {
    //     document.getElementById('kpi_gantt_popup')?.setAttribute('style', 'visibility:hidden')
    //     document.getElementsByClassName('cdk-overlay-pane custom-tooltip')[0]?.setAttribute('style', 'visibility:hidden')
    //     this.pendingInformation = [{
    //       heading: myData?.data.name + ' - Information',
    //       startDate: myData?.data?.startDate,
    //       endDate: myData?.data?.endDate,
    //       duration: myData?.data?.duration,
    //       codes: myData.statuscodes
    //     }]
    //     this.showPendingTooltip = true
    //     this.showGanttTooltip = false
    //     this.showPointerTooltip = false
    //     this.showChangeTooltip = false

    //     if (difference > 300) {
    //       d3.select("#d3Tooltip")
    //         .style('visibility', 'visible')
    //         .style('position', 'absolute')
    //         .style('top', (myY) + 'px')
    //         .style('left', (myX + 10) + 'px')
    //         .style('right', 'unset')
    //         .style('z-index', '99999')

    //     } else if (difference < 300) {
    //       d3.select("#d3Tooltip")
    //         .style('visibility', 'visible')
    //         .style('position', 'absolute')
    //         .style('top', (myY) + 'px')
    //         .style('right', (difference) + 'px')
    //         .style('left', 'unset')
    //         .style('z-index', '99999')
    //     }
    //   }
    //   else if (myData?.data?.children == undefined) {
    //     if (myData.data) {
    //       if (myData?.data?.transactiontype != undefined || myData?.data?.transactiontype != null || myData?.data?.transactiontype != '' || myData?.data?.transactiontype != 'unknown') {
    //         this.transactionName = myData.data.name
    //         this.getTransactionPopupDetails(100108, myData.data.transactiontype, myData.data.transactionid)

    //         if (difference > 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 300) + 'px')
    //             .style('left', (myX + 10) + 'px')
    //             .style('right', 'unset')
    //             .style('z-index', '99999')

    //         }
    //         else if (difference < 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 300) + 'px')
    //             .style('right', difference + 'px')
    //             .style('left', 'unset')
    //             .style('z-index', '99999')
    //         }
    //         this.showGanttTooltip = true
    //         this.showPendingTooltip = false
    //         this.showPointerTooltip = false
    //         this.showChangeTooltip = false


    //       }
    //     }

    //     else {
    //       d3.select("#d3Tooltip")
    //         .style('visibility', 'visible')
    //         .style('position', 'absolute')
    //         .style('top', (myY + 20) + 'px')
    //         .style('left', (myX + 10) + 'px')
    //         .style('z-index', '99999')

    //       this.pointerTooltipData = myData
    //       this.showPointerTooltip = true
    //       this.showGanttTooltip = false
    //       this.showPendingTooltip = false


    //     }
    //   }
    // }

    // else {
    //   if (this.transactionName && this.legendID != 'change') {
    //     if (myData.transactiontype != undefined || myData.transactiontype != null || myData.transactiontype != '' || myData.transactiontype != 'unknown') {
    //       this.getTransactionPopupDetails(100108, myData.transactiontype, myData.transactionid)
    //       if (difference > 300) {
    //         d3.select("#d3Tooltip")
    //           .style('visibility', 'visible')
    //           .style('position', 'absolute')
    //           .style('top', (myY - 300) + 'px')
    //           .style('left', (myX + 10) + 'px')
    //           .style('right', 'unset')
    //           .style('z-index', '99999')
    //       }
    //       else if (difference < 300) {
    //         d3.select("#d3Tooltip")
    //           .style('visibility', 'visible')
    //           .style('position', 'absolute')
    //           .style('top', (myY - 300) + 'px')
    //           .style('right', difference + 'px')
    //           .style('left', 'unset')
    //           .style('z-index', '99999')
    //       }
    //       document.getElementById('kpi_gantt_popup')?.setAttribute('style', 'visibility:hidden')
    //       document.getElementsByClassName('cdk-overlay-pane custom-tooltip')[0]?.setAttribute('style', 'visibility:hidden')
    //       this.showGanttTooltip = true
    //       this.showPendingTooltip = false
    //       this.showPointerTooltip = false
    //       this.showChangeTooltip = false
    //     }
    //   }
    //   else if (this.transactionName && this.legendID == 'change') {
    //     if (myData.transactiontype != undefined || myData.transactiontype != null || myData.transactiontype != '' || myData.transactiontype != 'unknown') {
    //       this.getTransactionPopupDetails(100108, myData.transactiontype, myData.transactionid)
    //       if (difference > 300) {
    //         d3.select("#d3Tooltip")
    //           .style('visibility', 'visible')
    //           .style('position', 'absolute')
    //           .style('top', (myY - 300) + 'px')
    //           .style('left', (myX + 10) + 'px')
    //           .style('right', 'unset')
    //           .style('z-index', '99999')
    //       }
    //       else if (difference < 300) {
    //         d3.select("#d3Tooltip")
    //           .style('visibility', 'visible')
    //           .style('position', 'absolute')
    //           .style('top', (myY - 300) + 'px')
    //           .style('right', difference + 'px')
    //           .style('left', 'unset')
    //           .style('z-index', '99999')
    //       }
    //       document.getElementById('kpi_gantt_popup')?.setAttribute('style', 'visibility:hidden')
    //       document.getElementsByClassName('cdk-overlay-pane custom-tooltip')[0]?.setAttribute('style', 'visibility:hidden')
    //       this.showGanttTooltip = false
    //       this.showPendingTooltip = false
    //       this.showPointerTooltip = false
    //       this.showChangeTooltip = true


    //     }

    //   }
    //   else if (myData?.data?.children != undefined) {
    //     document.getElementById('kpi_gantt_popup')?.setAttribute('style', 'visibility:hidden')
    //     document.getElementsByClassName('cdk-overlay-pane custom-tooltip')[0]?.setAttribute('style', 'visibility:hidden')
    //     this.pendingInformation = [{
    //       heading: myData?.data.name + ' - Information',
    //       startDate: myData?.data?.startDate,
    //       endDate: myData?.data?.endDate,
    //       duration: myData?.data?.duration,
    //       codes: myData.statuscodes
    //     }]
    //     this.showPendingTooltip = true
    //     this.showGanttTooltip = false
    //     this.showPointerTooltip = false
    //     this.showChangeTooltip = false

    //     if (difference > 300) {
    //       d3.select("#d3Tooltip")
    //         .style('visibility', 'visible')
    //         .style('position', 'absolute')
    //         .style('top', (myY - 30) + 'px')
    //         .style('left', (myX + 10) + 'px')
    //         .style('right', 'unset')
    //         .style('z-index', '99999')

    //     } else if (difference < 300) {
    //       d3.select("#d3Tooltip")
    //         .style('visibility', 'visible')
    //         .style('position', 'absolute')
    //         .style('top', (myY - 30) + 'px')
    //         .style('right', (difference) + 'px')
    //         .style('left', 'unset')
    //         .style('z-index', '99999')
    //     }
    //   }
    //   else if (myData?.data?.children == undefined) {
    //     if (myData.data) {
    //       if (myData?.data?.transactiontype != undefined || myData?.data?.transactiontype != null || myData?.data?.transactiontype != '' || myData?.data?.transactiontype != 'unknown') {
    //         this.transactionName = myData.data.name
    //         this.getTransactionPopupDetails(100108, myData.data.transactiontype, myData.data.transactionid)
    //         if (difference > 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 300) + 'px')
    //             .style('left', (myX + 10) + 'px')
    //             .style('right', 'unset')
    //             .style('z-index', '99999')
    //         }
    //         else if (difference < 300) {
    //           d3.select("#d3Tooltip")
    //             .style('visibility', 'visible')
    //             .style('position', 'absolute')
    //             .style('top', (myY - 300) + 'px')
    //             .style('right', difference + 'px')
    //             .style('left', 'unset')
    //             .style('z-index', '99999')
    //         }
    //         this.showGanttTooltip = true
    //         this.showPendingTooltip = false
    //         this.showPointerTooltip = false
    //         this.showChangeTooltip = false

    //       }
    //     }

    //     else {
    //       d3.select("#d3Tooltip")
    //         .style('visibility', 'visible')
    //         .style('position', 'absolute')
    //         .style('top', (myY - 30) + 'px')
    //         .style('left', (myX + 10) + 'px')
    //         .style('z-index', '99999')

    //       this.pointerTooltipData = myData
    //       this.showPointerTooltip = true
    //       this.showGanttTooltip = false
    //       this.showPendingTooltip = false
    //       this.showChangeTooltip = false


    //     }
    //   }
    // }
  }

  // Tooltip Hide

  hideCustomTooltip() {
    this.dynamicCustomTooltipService.hideTooltip();
  }

  toolTipHide() {
    this.showGanttTooltip = false
    this.showChangeTooltip = false
  }
  tooltipPendingHide() {
    this.showPendingTooltip = false
  }

  hideTooltip(myType?: any): void {
    this.showPointerTooltip = false;
    if(myType == 'averageDot' || myType == 'groupFullName') {
      this.hideCustomTooltip();
    }
  }

  //--- Angular LifeCycle ----//


  ngOnInit(): void {
    if (this.chartContainerId) {
      this.divId = this.chartContainerId;
    }
    this.showCircleFlags = this.widgets?.config?.circle_flags?.controls
    this.monthsVisible = this.widgets?.config?.months_visible
    this.widgetEndpoint = this.widgets?.config?.widget_endpoint
    this.totalLegendData = []
    this.selctedLegendData = []
    this.currentLegendSelections = []
    this.dataService.radarData.next('');


    if (!this.embeddedMode) {
      this.router.params.subscribe((p: any) => {
        this.cardName = p['cardName'] || '';

        this.router.queryParams.subscribe((params: any) => {
          let decryptedParams = this.navigationService.decryptData(params);
          this.clientKey = decryptedParams["cl_key"];
          this.moduleKey = decryptedParams["md_key"];
          this.dashboardKey = decryptedParams["ds_key"];
          this.patientId = decryptedParams["cardId"];
          this.cardId = decryptedParams["cardId"];
        });
      });
    }

    if (!this.embeddedMode) {
      this.reqSubcription.push(this.dataService.expandCollapseChart.subscribe((resp: boolean) => {
      if (this.currentLegendSelections.length == 0) {
        this.selctedLegendData = []
      } else if (this.currentLegendSelections.length == this.selctedLegendData.length) {
        this.selctedLegendData = []
      } else {
        this.selctedLegendData = this.totalLegendData
        this.currentLegendSelections.forEach((j: any) => {
          this.selctedLegendData = this.selctedLegendData.filter((i: any) => i == j)
        })
      }
      if (resp == true) {
        this.collapse = "expand"
        this.showGanttTooltip = false;
        this.showPendingTooltip = false;
        this.showPointerTooltip = false;
      } else {
        this.collapse = "collapse"
        this.showGanttTooltip = false;
        this.showPendingTooltip = false;
        this.showPointerTooltip = false;
      }

      this.props.defaultExpend = undefined;
      this.props.expandCollapseAll = this.collapse;
      this.hideCustomTooltip();
      if(this.ganttChartData?.length>0) {
        setTimeout(() => {this.plotChart();}, 100);
      }

      this.props.expandCollapseAll = this.collapse;
      setTimeout(() => {
        this.plotChart()
      }, 100);
    }))
    }
    // API Functions Called At Time of Loading — skeleton is created in ngAfterViewInit once the host exists.
    if (this.embeddedMode) {
      this.loadEmbeddedData();
    } else {
      this.reqSubcription.push(
        this.filterServiceOld.filterQuery.subscribe((query: any) => {
          // this.getDispenseData();
          if(this.widgets?.config?.['headerAccess']?.name != 'No') {
            this.getProgramsData();
          }
          // this.getkpiData();
          // this.getRefillData();
          setTimeout(() => {
            this.getGanttChartData();
          }, 200);
        })
      )

      // status changes
      this.reqSubcription.push(
        this.filterServiceOld.refreshStatusChangeData.subscribe((res: any) => {
          this.getGanttChartData();
        })
      );
    }

  }

  @HostListener("window:resize", ["$event"])
  onResize(event: Event) {
    if (this.embeddedMode) {
      return;
    }
    setTimeout(() => {
      this.plotChart()
    }, 200);
  }

  private getNodeExpandKey(d: any): string {
    if (d?.data?.hierarchyPath) {
      const key = String(d.data.hierarchyPath);
      return this.embeddedMode ? `${this.divId}|${key}` : key;
    }
    const parts: string[] = [];
    let node = d;
    while (node?.depth > 0) {
      let label: string | undefined;
      if (node.data?.fullName != null) {
        try {
          label = JSON.parse(node.data.fullName);
        } catch {
          label = node.data?.name;
        }
      } else if (node.data?.name) {
        label = node.data.name.replace(/\.\.$/, '');
      }
      if (label) {
        parts.unshift(String(label));
      }
      node = node.parent;
    }
    const key = parts.join('|');
    return this.embeddedMode ? `${this.divId}|${key}` : key;
  }

  private applyTreeExpandOverrides(chartData: any): void {
    chartData.descendants().forEach((d: any) => {
      const nodeKey = this.getNodeExpandKey(d);
      const override = this.treeNodeExpandOverrides.get(nodeKey);
      if (override === 'expanded' && d._children) {
        d.children = d._children;
        d._children = null;
      } else if (override === 'collapsed' && d.children) {
        d._children = d.children;
        d.children = null;
      }
    });
  }

  setPlotReason(reason: string): void {
    this.lastPlotReason = reason;
  }

  hasRenderedTree(): boolean {
    const host = this.getChartHost();
    if (!host) {
      return false;
    }
    const treeSelector = `[class*="treeGroup${this.divId}"]`;
    return !!host.querySelector(treeSelector);
  }

  /** Unique SVG fragment ids when multiple embedded charts share a page (Scraii AI). */
  private embeddedSvgId(base: string): string {
    return this.embeddedMode ? `${base}_${this.divId}` : base;
  }

  private chartSelectAll(mySvg: any, selector: string): any {
    return this.embeddedMode ? mySvg.selectAll(selector) : d3.selectAll(selector);
  }

  private chartSelect(mySvg: any, selector: string): any {
    return this.embeddedMode ? mySvg.select(selector) : d3.select(selector);
  }

  triggerTreeExpand(event: any, d: any): void {
    this.handleTreeExpand(event, d);
  }

  /** Expand all tree nodes (instance-scoped; safe for embedded SCRAII mode). */
  expandAllTree(): void {
    this.treeNodeExpandOverrides.clear();
    this.collapse = 'expand';
    this.showGanttTooltip = false;
    this.showPendingTooltip = false;
    this.showPointerTooltip = false;
    if (this.props) {
      this.props.defaultExpend = undefined;
      this.props.expandCollapseAll = this.collapse;
    }
    this.hideCustomTooltip();
    if (this.ganttChartData?.length > 0) {
      this.setPlotReason('tree-expand-collapse');
      this.plotChart();
    }
  }

  /** Collapse tree to default depth (instance-scoped; safe for embedded SCRAII mode). */
  collapseAllTree(): void {
    this.treeNodeExpandOverrides.clear();
    this.collapse = 'collapse';
    this.showGanttTooltip = false;
    this.showPendingTooltip = false;
    this.showPointerTooltip = false;
    if (this.props) {
      this.props.expandCollapseAll = this.collapse;
      this.props.defaultExpend = this.widgets?.config?.['defaultExpend'] || 0;
    }
    this.hideCustomTooltip();
    if (this.ganttChartData?.length > 0) {
      this.setPlotReason('tree-expand-collapse');
      this.plotChart();
    }
  }

  private handleTreeExpand(event: any, d: any): void {
    if (!this.embeddedPlotContext) {
      return;
    }
    const now = Date.now();
    if (now - this.lastTreeExpandAt < 200) {
      return;
    }
    this.lastTreeExpandAt = now;
    event?.stopPropagation?.();
    const { redraw, hostEl } = this.embeddedPlotContext;
    const beforeCollapsed = d.children === null;
    if (d.children === null) {
      d.children = d._children;
      d._children = null;
    } else {
      d._children = d.children;
      d.children = null;
    }
    const nodeKey = this.getNodeExpandKey(d);
    this.treeNodeExpandOverrides.set(nodeKey, d.children === null ? 'collapsed' : 'expanded');
    redraw();
    // In embedded (non-fullscreen) mode, jump the scroll so the newly expanded
    // node is visible. Skip this in fullscreen: the whole SVG scrolls inside
    // .sgc-root, so jumping to the expanded row scrolls the header (dispense
    // hover markers + circle-flag icon) off the top. In fullscreen we keep the
    // current scroll position so the header stays put.
    if (this.embeddedMode && !this.isFullscreen && beforeCollapsed) {
      const rowTop = d.yPos;
      if (rowTop != null) {
        const scrollEl = (hostEl.closest('.sgc-root')
          || hostEl.closest('.scraii-ai-chart-wrapper')
          || hostEl.closest('.scraii-ai-gantt-container')) as HTMLElement | null;
        if (scrollEl) {
          scrollEl.scrollTop = Math.max(0, rowTop - 80);
        }
      }
    }
  }

  private syncEmbeddedAncestors(hostEl: HTMLElement, totalHeight: number): void {
    hostEl.style.minHeight = `${totalHeight}px`;
    const isFullscreen = !!hostEl.closest('.scraii-ai-fullscreen');
    if (isFullscreen) {
      return;
    }
    const wrapperEl = hostEl.closest('.scraii-ai-chart-wrapper') as HTMLElement | null;
    const containerEl = hostEl.closest('.scraii-ai-gantt-container') as HTMLElement | null;
    const sgcRoot = hostEl.closest('.sgc-root') as HTMLElement | null;
    if (wrapperEl) {
      wrapperEl.style.minHeight = `${totalHeight}px`;
      wrapperEl.style.height = 'auto';
    }
    if (containerEl) {
      containerEl.style.minHeight = `${totalHeight + 56}px`;
      containerEl.style.height = 'auto';
    }
    if (sgcRoot && sgcRoot !== hostEl) {
      sgcRoot.style.minHeight = `${totalHeight}px`;
    }
  }

  ngAfterViewInit(): void {
    this.chartViewReady = true;
    const host = this.getChartHost();
    if (host && this.chartContainerId) {
      host.id = this.divId;
    }
    this.ensureChartSkeleton();
    if (this.pendingPlotChart || (this.embeddedMode && this.ganttChartData?.length > 0)) {
      this.pendingPlotChart = false;
      setTimeout(() => this.plotChart(), 0);
    }
  }

  private getChartHost(): HTMLElement | null {
    return (this.GanttChartContainer?.nativeElement as HTMLElement) || null;
  }

  /** Stable palette colour for a label (embedded fallback for unmapped colours). */
  paletteColorFor(key: string): string {
    const str = (key ?? '').toString();
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    }
    return GANTT_RANDOM_COLOR_PALETTE[hash % GANTT_RANDOM_COLOR_PALETTE.length];
  }

  private ensureChartSkeleton(): boolean {
    const host = this.getChartHost();
    if (!host) {
      return false;
    }
    if (!host.querySelector('svg')) {
      this.initiateCharts(host);
    }
    return !!host.querySelector('svg');
  }

  private schedulePlotChart(): void {
    if (!this.chartViewReady) {
      this.pendingPlotChart = true;
      return;
    }
    if (!this.ensureChartSkeleton()) {
      this.pendingPlotChart = true;
      return;
    }
    setTimeout(() => this.plotChart(), 100);
  }

  private getEmbeddedPatientId(rows: any[] | null | undefined): string | null {
    const row = Array.isArray(rows) ? rows[0] : null;
    const id = row?.patientid ?? row?.patient_id ?? row?.patientId;
    return id != null ? String(id) : null;
  }

  private isSameChartSnapshot(resp: any[] | null | undefined): boolean {
    if (!Array.isArray(resp) || !resp.length || !Array.isArray(this.ganttChartData) || !this.ganttChartData.length) {
      return false;
    }
    if (resp.length !== this.ganttChartData.length) {
      return false;
    }
    const nextId = this.getEmbeddedPatientId(resp);
    const currentId = this.getEmbeddedPatientId(this.ganttChartData);
    return !!nextId && nextId === currentId;
  }

  /** Re-ingest wrapper inputs when the child missed the first binding cycle. */
  ensureEmbeddedDataLoaded(): void {
    this.loadEmbeddedData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.embeddedMode) {
      if (changes['externalChartData']) {
        this.loadEmbeddedData();
      } else if (changes['externalProgramData']) {
        const showHeader = this.widgets?.config?.['headerAccess']?.name != 'No';
        if (showHeader && this.externalProgramData?.length) {
          this.applyProgramData(this.externalProgramData);
        }
      }
      if (changes['compactLayout'] && !changes['compactLayout'].firstChange && this.ganttChartData?.length > 0) {
        this.schedulePlotChart();
      }
    }

    if (changes['isFullscreen'] && changes['isFullscreen'].currentValue != changes['isFullscreen'].previousValue) {
      setTimeout(() => {
        this.hideCustomTooltip();
        this.plotChart()
      }, 100);
    }

    // document.addEventListener('fullscreenchange', (event) => {
      // if (this.currentLegendSelections.length == 0) {
      //   this.selctedLegendData = [];
      // } else if (this.currentLegendSelections.length == this.selctedLegendData.length) {
      //   this.selctedLegendData = []
      // } else {
      //   this.selctedLegendData = this.totalLegendData
      //   this.currentLegendSelections.forEach((j: any) => {
      //     this.selctedLegendData = this.selctedLegendData.filter((i: any) => i == j)
      //   })
      // }


      // if (document.fullscreenElement) {
      //   document.getElementsByClassName('cdk-overlay-pane custom-tooltip')[0]?.setAttribute('style', 'visibility:hidden')
      //   document.getElementById('completeGanttChart')?.setAttribute('style', 'height:100vh;overflow-y:auto;overflow-x:hidden;background:#ffff')
      //   this.showGanttTooltip = false;
      //   this.showPendingTooltip = false;
      //   this.showPointerTooltip = false;
      //   this.isFullscreen = true
      // } else {
      //   this.showGanttTooltip = false;
      //   this.showPendingTooltip = false;
      //   this.showPointerTooltip = false;
      //   this.isFullscreen = false
       
      //   document.getElementsByClassName('cdk-overlay-pane custom-tooltip')[0]?.setAttribute('style', 'visibility:hidden')
      //   document.getElementById('completeGanttChart')?.setAttribute('style', 'height:auto;overflow-y:unset;background:#ffff;overflow-x:unset')
      // }

      // setTimeout(() => {
        // this.hideCustomTooltip();
    //     this.plotChart()
    //   }, 100);
    // })
  }

  // ---- Chart Configuration ----//
  plotChart(): void {
    if (this.plotChartInProgress) {
      this.pendingPlotChart = true;
      return;
    }
    this.plotChartInProgress = true;
    this.currentLegendSelections = [];
    try {
      this.plotChartImpl();
    } catch {
      // plotChart errors are non-fatal; chart will retry on next layout pass
    } finally {
      this.plotChartInProgress = false;
      if (this.pendingPlotChart) {
        this.pendingPlotChart = false;
        setTimeout(() => this.plotChart(), 0);
      }
    }
  }

  // chart svg plotChart rendering
  private plotChartImpl(): void {
    const myChart = this;
    const myClass = myChart.divId;
    const isPortrait = window.innerHeight > window.innerWidth;
    const hostEl = this.getChartHost();
    if (!hostEl) {
      this.pendingPlotChart = true;
      return;
    }
    if (!this.ensureChartSkeleton()) {
      this.pendingPlotChart = true;
      return;
    }
    const mySvg: any = d3.select(hostEl).select('svg');
    const brushClipId = myChart.embeddedSvgId('ganttBrushClip');
    const brushClipRectId = myChart.embeddedSvgId('ganttBrushClipRect');
    const dropShadowId = myChart.embeddedSvgId('drop-shadow');
    const scopeAll = (selector: string) => myChart.chartSelectAll(mySvg, selector);
    const scopeSelect = (selector: string) => myChart.chartSelect(mySvg, selector);
    const containerWidth = hostEl.getBoundingClientRect()?.width || 0;
    const svgWidth = mySvg?.node()?.getBoundingClientRect()?.width || 0;
    const measuredWidth = this.embeddedMode
      ? (containerWidth || svgWidth || 0)
      : (svgWidth || containerWidth || 0);
    if (this.embeddedMode && measuredWidth < 10) {
      if (!this.embeddedWidthRetryPending) {
        this.embeddedWidthRetryPending = true;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.embeddedWidthRetryPending = false;
            this.plotChart();
          });
        });
      }
      return;
    }
    const width = measuredWidth || 1200;
    if (this.embeddedMode && this.lastEmbeddedPlotWidth > 0) {
      const widthDelta = Math.abs(width - this.lastEmbeddedPlotWidth);
      const skipRelayoutReasons =
        this.lastPlotReason === 'width-change' || this.lastPlotReason === 'relayout';
      const forceRelayout =
        this.lastPlotReason === 'fullscreen-toggle' || this.lastPlotReason === 'tree-expand-collapse';
      if (widthDelta < 2 && skipRelayoutReasons && !forceRelayout) {
        return;
      }
    }
    this.lastEmbeddedPlotWidth = width;
    mySvg.attr('width', width);
    const isCompact = this.embeddedMode && this.compactLayout;
    const margins = isCompact
      ? { left: 16, leftExtra: 115, right: 16, top: 210, bottom: 50, mid: 16, brush: 26, headerHeight: 100, headerLeft: 130 }
      : { left: 20, leftExtra: 200, right: 20, top: 210, bottom: 50, mid: 20, brush: 30, headerHeight: 120, headerLeft: 220 };
    const axisLabelSize = isCompact ? 8 : 10;
    const fontSizes: any = isCompact ? { 1: 11, 2: 9, above: 8 } : { 1: 14, 2: 12, above: 10 };
    const headerTitleSize = isCompact ? 11 : 14;
    const smallLabelSize = isCompact ? 8 : 10;
    const measureLabelSize = isCompact ? 9 : 11;
    const measureSmallSize = isCompact ? 9.5 : 11.5;
    const recordGenMeasureSize = isCompact ? 9 : 12;
    const legendLabelSize = isCompact ? 9 : 12;
    const expandCollapsedSize = isCompact ? 10 : 14;
    const expandExpandedSize = isCompact ? 14 : 22;
    const rowHeight = isCompact ? 24 : 28;
    const timeRectHeight = isCompact ? 16 : 20;
    const kpiHeight = 30;
    let dayWidth = 12;
    const changeIconPath = "M7.5 9.43711C8.56067 8.66434 9.25 7.41257 9.25 5.9998C9.25 3.65259 7.34721 1.7498 5 1.7498H4.75M5 10.2498C2.65279 10.2498 0.75 8.34701 0.75 5.9998C0.75 4.58704 1.43933 3.33527 2.5 2.5625M4.5 11.1998L5.5 10.1998L4.5 9.1998M5.5 2.7998L4.5 1.7998L5.5 0.799805";
    const changeIconViewBox = "0 0 10 12";
    //this is a quick fix as Sitaram did mention he might change the date format
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const dateFormat = d3.timeFormat('%d %b %Y')
    let programData: any = myChart.headerChartData.program;

    programData.map((m: any) => m[myChart.props.startDateVar] = new Date(m[myChart.props.startDateVar]));
    programData.map((m: any) => m[myChart.props.endDateVar] = new Date(m[myChart.props.endDateVar]));
    programData.map((m: any) => m.includeEnd = programData.find((f: any) => String(f[myChart.props.startDateVar]) === String(m[myChart.props.endDateVar])) === undefined);
    // const totalProgramDays = programData.length === 0 ? 0 : d3.sum(programData, (d: any) => d.gapdays==false&&d.days);
    const totalProgramDays = programData.length === 0 ? 0 : d3.sum(programData, (d: any) => d.gapdays != true ? d.days : 0);

    let programSet: any = new Set();
    programData.forEach((d: any) => programSet.add(d[myChart.props.programVar]))
    programSet = Array.from(programSet);
    
    // Assign colors dynamically (case-insensitive)
    const programColourScale = d3.scaleOrdinal()
      .domain(programSet)
      .range(programSet.map((d: any) => myChart.props.programColorMapping[d?.toLowerCase()]
        || myChart.props.programColorMapping['default']
        || (myChart.embeddedMode ? myChart.paletteColorFor('program:' + d) : '#5b54e3')));

    // const programColourScale = d3.scaleOrdinal().domain(programSet).range(myChart.props.programColors);

    let dispenseData: any = myChart.headerChartData.dispense;
    dispenseData.map((m: any) => m[myChart.props.dispenseTransactionDateVar] = new Date(m[myChart.props.dispenseTransactionDateVar]));

    let kpiData: any = myChart.headerChartData.kpi;

    // 

    kpiData.map((m: any) => m[myChart.props.startDateVar] = new Date(m[myChart.props.startDateVar]));
    kpiData.map((m: any) => m[myChart.props.endDateVar] = new Date(m[myChart.props.endDateVar]));
    kpiData.map((m: any) => m.dayCount = d3.timeDay.count(m[myChart.props.startDateVar], m[myChart.props.endDateVar]));

    kpiData = kpiData.sort((a: any, b: any) => d3.ascending(a[myChart.props.startDateVar], b[myChart.props.startDateVar]));

    let kpiGroups: any = [];
    kpiData.forEach((d: any, i: any) => {
      if(i === 0){
        kpiGroups.push({
          dateRange: [d[myChart.props.startDateVar], d[myChart.props.endDateVar]],
          data: [d]
        })
      } else {
        const dateOverlap = kpiGroups.find((f: any) => d[myChart.props.startDateVar] < f.dateRange[1] && d[myChart.props.endDateVar] > f.dateRange[0])
        if(dateOverlap === undefined){
          if(kpiGroups[i-1]) {
            kpiGroups[i-1].data.push(d);
            kpiGroups[i-1].dateRange[0] = Math.min(d[myChart.props.startDateVar],kpiGroups[i-1].dateRange[0]);
            kpiGroups[i-1].dateRange[1] = Math.max(d[myChart.props.endDateVar],kpiGroups[i-1].dateRange[1]);
          }
        } else {
          kpiGroups.push({
            dateRange: [d[myChart.props.startDateVar], d[myChart.props.endDateVar]],
            data: [d]
          })
        }
      }
    })
    const ext = d3.extent(programData,(d: any) => d[myChart.props.startDateVar])
    // const programExtent = programData.length === 0 ? [new Date(),new Date()] : programData.length === 1 ? [programData[0][myChart.props.startDateVar],programData[0][myChart.props.endDateVar]] : ext;
    const dispenseExtent = dispenseData.length === 0 ? [new Date(),new Date()] : d3.extent(dispenseData,(d: any) => d[myChart.props.dispenseTransactionDateVar]);
    const kpiExtent = kpiData.length === 0 ? [new Date(),new Date()] : d3.extent(kpiData,(d: any) => d[myChart.props.startDateVar]);

    //@ts-ignore
    // let headerXMin: any = Math.min(programExtent[0], dispenseExtent[0], kpiExtent[0]);
    // //@ts-ignore
    // let headerXMax: any = Math.max(programExtent[1], dispenseExtent[1], kpiExtent[1]);
    
    // Only for Program
    const startExtent = d3.extent(programData, (d: any) => new Date(d[myChart.props.startDateVar]));
    const endExtent = d3.extent(programData, (d: any) => new Date(d[myChart.props.endDateVar]));

    const programExtent = programData?.length === 0 
            ? [new Date(), new Date()]
            : programData.length === 1
            ? [new Date(programData[0][myChart.props.startDateVar]), new Date(programData[0][myChart.props.endDateVar])]
            : [startExtent[0], endExtent[1]];

    let headerXMinProgram = programExtent[0]?.getTime() ?? new Date().getTime();
    let headerXMaxProgram = programExtent[1]?.getTime() ?? new Date().getTime();

    const xScaleHeader = d3
      .scaleTime()
      .range([0, width - margins.headerLeft - margins.right])
      .domain([new Date(headerXMinProgram), new Date(headerXMaxProgram)]);

    // const xScaleHeader = d3.scaleTime().range([0, width - margins.headerLeft - margins.right - 100]).domain([headerXMin, headerXMax]);
    margins.headerHeight = myChart.props.headerAccess ? margins.headerHeight : 55;
    margins.top = margins.headerHeight + (kpiGroups.length * kpiHeight) + 20
    
    if(!myChart.ganttChartData) {
      return;
    }
    
    myChart.ganttChartData.map((m: any) => m.TransactionDate = new Date(m[myChart.props.transactionDateVar]));
    myChart.ganttChartData.map((m: any) => m.endDate = new Date(m[myChart.props.transactionEndDateVar]));
    let {chartData, flagData} = formatData();

    let dateMin: any = d3.min(chartData.descendants(), (d: any) => d.data.startDate);
    let dateMax: any = d3.max(chartData.descendants(), (d: any) => d.data.endDate);

    if (dateMin == null || dateMax == null) {
      return;
    }


    if(dateMax < new Date()){
      dateMax = new Date();
    }
    let dateExtent: any = [dateMin, dateMax];
    dateExtent[1] = d3.timeWeek.offset(dateMax, 1);

    let flagSet: any = new Set();
    flagData.forEach((d: any) => flagSet.add(d[myChart.props.transactionTypeVar]));
    dispenseData.forEach((d: any) => flagSet.add(d.type));
    flagSet = Array.from(flagSet);
    if(flagSet.find((f: any) => f.toLowerCase().includes("change"))  !== undefined){
      flagSet = flagSet.filter((f: any) => f.toLowerCase().includes("change") === false);
      flagSet.push("change");
    }
    const flagColours = myChart.props.colors;
    // Embedded fallback: ensure every legend flag has a colour so nothing renders
    // un-stroked when the widget config omits it. Keyed by lowercased type to
    // match the lookups below (flagColours[type.toLowerCase()]).
    if (myChart.embeddedMode) {
      flagSet.forEach((f: any) => {
        const key = (f ?? '').toString().toLowerCase();
        if (key && !flagColours[key]) {
          flagColours[key] = myChart.paletteColorFor('flag:' + key);
        }
      });
    }
    if(myChart.props.newLegendSelections !== null){
      myChart.props.newLegendSelections.forEach((d: any) => {
        myChart.currentLegendSelections.push(d.toLowerCase());
      });
    } else {
      flagSet.forEach((m: any) => myChart.currentLegendSelections.push(m.replace(/ /g, '').toLowerCase()))
    }
     const brush: any = d3.brushX()
      .handleSize(10)
      .extent([[0, 0], [width - margins.left - margins.right - margins.leftExtra , margins.brush]])
      .on('start brush end', brushed);

    const xScaleBrush = d3.scaleTime().range([0, width - margins.left - margins.right - margins.leftExtra]).domain(dateExtent);
    const xScaleAll = d3.scaleTime().range([0, width - margins.left  - margins.right - margins.leftExtra]).domain(dateExtent);
    const xScaleSelected: any = d3.scaleTime().range([0, width - margins.left - margins.right - margins.leftExtra]).domain(dateExtent);
    let chartDates: any = new Set();
    chartData.descendants().forEach((d: any) => {
      if(d.data.TransactionDate !== 'NULL'){
        chartDates.add(d.data.TransactionDate)
      }})
    chartDates = Array.from(chartDates);

    chartData.descendants().map((m: any) => m.dateExtent = [m.data.startDate, m.data.endDate]);

    const startingDepth = myChart.props.expandCollapseAll === null  || myChart.props.expandCollapseAll === 'collapse' ? (myChart.props.defaultExpend ? myChart.props.defaultExpend : 0) : 7;
    chartData.descendants().forEach((d: any) => {
      if (d.depth > startingDepth) {
        if (d.children) {
          d._children = d.children;
          d.children = null;
        }
      } else if (d.children) {
        d._children = null;
      }
    });
    if (myChart.embeddedMode) {
      myChart.applyTreeExpandOverrides(chartData);
    }

    // let brushStartX = 0;
    // let brushEndX = width - margins.left - margins.right - margins.leftExtra;
    // const finalDate: any = d3.max(chartData.descendants(), (d: any) => d.data.endDate);
    // const brushStartDate = d3.timeMonth.offset(finalDate, -(myChart.props.monthsVisibleStart==0 ? 1 : myChart.props.monthsVisibleStart));
    
    // // 

    // if(brushStartDate > dateMin){
    //   brushStartX = xScaleAll(brushStartDate);
    //   brushEndX = xScaleAll(finalDate);
    // } else if (d3.timeMonth.count(finalDate, new Date())){
    //   brushEndX = xScaleAll(finalDate);
    // }

    let brushStartX = 0;
    let brushEndX = width - margins.left - margins.right - margins.leftExtra;

    // Set finalDate to the maximum date from data or the current date, whichever is earlier
    const chartEndDate = d3.max(chartData.descendants(), (d: any) => d.data.endDate) as Date | undefined;
    const finalDate: Date = d3.min(
      [chartEndDate, new Date()].filter((d): d is Date => d instanceof Date) // Filter to ensure only valid Dates
    )!;

    // Calculate the brush start date
    const brushStartDate = d3.timeMonth.offset(
      finalDate,
      -(myChart.props.monthsVisibleStart == 0 ? 1 : myChart.props.monthsVisibleStart)
    );

    
    // if (brushStartDate > dateMin) {
    //   // Align brush start and end within the valid range
    //   brushStartX = 0 // xScaleAll(brushStartDate);
    //   brushEndX = xScaleAll(finalDate);
    // } else {
    //   // Ensure brushEndX always represents the current date
    //   brushEndX = xScaleAll(new Date());
    // }

    // console.log(brushStartDate, finalDate, brushEndX)

    let chartHeight =  chartData.descendants().length * rowHeight;

    d3.select('.headerTitle' + myClass)
      .style('display', this.props.headerAccess ? 'block' : 'none')
      .attr('fill', '#101D42')
      .attr('x', margins.left)
      .attr('y', (margins.headerHeight/2) - 2)
      .attr('font-size', headerTitleSize)
      .attr('font-family','Poppins')
      .attr('font-weight',500)
      .text('Days on therapy');

    d3.select('.headerTitleValue' + myClass)
      .style('display', this.props.headerAccess ? 'block' : 'none')
      .attr('fill', '#101D42')
      .attr('x', margins.left)
      .attr('y', (margins.headerHeight/2) + (isCompact ? 11 : 14))
      .attr('font-size', headerTitleSize)
      .attr('font-family','Poppins')
      .attr('font-weight',600)
      .text(totalProgramDays + ' days');

    d3.select('.headerBackgroundRect' + myClass)
      .style('display', this.props.headerAccess ? 'block' : 'none')
      .attr('fill', '#E8EAEE')
      .attr('height', 8)
      .attr('rx', 4)
      .attr('ry', 4)
      .attr('y', (margins.headerHeight/2) - 4)
      .attr('x', margins.headerLeft)
      .attr('width', width - margins.headerLeft - margins.right);

    d3.select('#brushGroup' + myClass).select('.overlay')
      .style('fill', 'transparent');

    d3.select('.brushBackgroundRect' + myClass)
      .attr('fill', '#F6F8FB')
      .attr('width', width - margins.left - margins.right - margins.leftExtra)
      .style('height', margins.brush);

    d3.select('.chartBackgroundRect' + myClass)
      .attr('fill', '#F6F8FB');

    d3.select('#brushGroup' + myClass)
      .call(brush)
      .call(brush.move, [brushStartX, brushEndX])
      .attr('transform', 'translate(' + (margins.left + margins.leftExtra) + ',' + (margins.top + chartHeight + margins.mid) + ')');

    d3.selectAll('#brushGroup' + myClass)
      .selectAll('.selection')
      .attr('fill', '#1363DF')
      .attr('fill-opacity', '0.05')
      .style('stroke', '#1363DF')
      .style('stroke-width', '1');

    const brushChartGroup = mySvg
      .select('#brushChartGroup' + myClass)
      .selectAll('.brushChartItems' + myClass)
      .data(chartData.children)
      .join(function(group: any): any {
        const enter = group.append('g').attr('class', 'brushChartItems' + myClass);
        enter.append('rect').attr('class', 'brushTimeRect');
        return enter;
      });

    brushChartGroup.select(".brushTimeRect")
      .attr('x', (d: any) => xScaleBrush(d.dateExtent[0]))
      .attr('y', (d: any, i: any) => i * (margins.brush/(d.parent.children.length)) + 1.5)
      .attr('fill', (d: any) => myChart.props.groupColours[d.data.name] === undefined ? myChart.props.groupColours["default"]: myChart.props.groupColours[d.data.name])
      .attr('height', (d: any) => (margins.brush/d.parent.children.length) - 3)
      .attr('width', (d: any) => xScaleBrush(d.dateExtent[1]) - xScaleBrush(d.dateExtent[0]));

    function toggleNodeExpand(event: any, d: any): void {
      myChart.handleTreeExpand(event, d);
    }
    myChart.activeTreeToggle = toggleNodeExpand;

    function bindExpandClick(selection: any): void {
      if (myChart.embeddedMode) {
        selection.on('click', null);
        return;
      }
      selection.on('click', (event: any, d: any) => toggleNodeExpand(event, d));
    }

    function drawChart(){

      let treeData = chartData.descendants().filter((f: any) => f.depth > 0);

      chartHeight = (treeData.length * rowHeight);

      treeData = treeData.sort((a: any, b: any) => d3.ascending(a.data.descendantIndex, b.data.descendantIndex))
      mySvg.attr('height', chartHeight  + margins.top + margins.bottom + margins.mid + margins.brush);

      treeData.forEach((d: any, i: any) => {
         d.startLeft = margins.left + ((d._children === undefined ? d.depth - 2 : d.depth - 1) * 10);
         d.yPos = (margins.top + (i * rowHeight));
         const textX = d._children === undefined ? 10 : 18;
         const fontSize = d.depth > 2 ? fontSizes["above"] : fontSizes[d.depth];
         const myLeftWidth = d.startLeft + measureWidth(d.data.name, fontSize+1) ;
         if(myLeftWidth > (margins.leftExtra + margins.left)){
           const nameRequiredLength = margins.leftExtra - (d.startLeft + textX) + 10;
           let textWidth = measureWidth(d.data.name, fontSize+1);
           d.data.fullName = JSON.stringify(d.data.name);
           // stores full name (for tooltip)
           while(textWidth > nameRequiredLength){
             // loops through and takes one letter off the end of the name
             // until it is the correct length
             d.data.name = d.data.name.substr(0, d.data.name.length - 1);
             textWidth = measureWidth(d.data.name, fontSize+1);
           }
           d.data.name = d.data.name + ".."
         }
      })

      xScaleSelected.range([0, width - margins.left - margins.right - margins.leftExtra]);

      d3.select('.todayLineTriangle' + myClass)
        .attr('stroke', '#1363DF')
        .attr('d', 'M-2,0 L2,0 L0,1.5 Z')
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra + xScaleSelected(new Date())) + ',' + (margins.top -2) + ')');

      d3.select('.todayLine' + myClass)
        .attr('stroke', '#1363DF')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,2')
        .attr('x1', margins.left +  margins.leftExtra + xScaleSelected(new Date()))
        .attr('x2', margins.left +  margins.leftExtra + xScaleSelected(new Date()))
        .attr('y1', margins.top + 2)
        .attr('y2', margins.top + chartHeight);

      d3.selectAll('.brushChartItems' + myClass)
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra) + ',' + (margins.top + chartHeight + margins.mid) + ')');

      d3.select('.chartBackgroundRect' + myClass)
        .attr('fill', '#F6F8FB')
        .attr('width', width - margins.left - margins.right - margins.leftExtra)
        .style('height', chartHeight)
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra) + ',' + margins.top + ')');

      d3.select('.brushBackgroundRect' + myClass)
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra) + ',' + (margins.top + chartHeight + margins.mid) + ')');

      // dates header group
      let treeLayer = mySvg.select('#treeLayer' + myClass);
      if (treeLayer.empty()) {
        treeLayer = mySvg.append('g').attr('id', 'treeLayer' + myClass);
      }
      const treeGroup = treeLayer
        .selectAll('.treeGroup' + myClass)
        .data(treeData)
        .join(function(group: any): any {
          const enter = group.append('g').attr('class', 'treeGroup' + myClass);
          enter.append('circle').attr('class', 'expandHitArea');
          enter.append('circle').attr('class', 'expandCircle');
          enter.append('line').attr('class', 'expandLine');
          enter.append('text').attr('class', 'expandLabel');
          enter.append('text').attr('class', 'nodeLabel');
          enter.append('line').attr('class', 'nodeLineLeft');
          enter.append('line').attr('class', 'nodeHorizontalLine');
          return enter;
        });

      treeGroup.attr('transform', (d: any) => 'translate(' + d.startLeft + ',' + d.yPos + ')');

      treeGroup.select('.nodeHorizontalLine')
        .attr('x1', 0)
        .attr('x2', (d: any) => margins.leftExtra  - d.startLeft)
        .attr('y1', timeRectHeight)
        .attr('y2', timeRectHeight)
        .attr('stroke-width', 1)
        .attr('stroke', '#F0F3F6');

      treeGroup.select('.expandLine')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', 16)
        .attr('y2', (d: any) => d.children === undefined || d.children === null ? 0 : (d.children.length * rowHeight) + 8)
        .attr('stroke-width', (d: any) => d.children === undefined || d.children === null ? 0 : (d.children.find((f: any) => f.data.children === undefined) === undefined ? 0 : 1))
        .attr('stroke', '#8A98AB');

      treeGroup.select('.nodeLineLeft')
        .attr('x1', (d: any) => d.data.children === undefined ? 7 : 0)
        .attr('x2', 0)
        .attr('y1', 8)
        .attr('y2', 8)
        .attr('stroke-width', 1)
        .attr('stroke', '#8A98AB');

      treeGroup.select('.expandHitArea')
        .attr('cy', 8)
        .attr('r', (d: any) => (d._children || d.children) ? 14 : 0)
        .attr('fill', 'transparent')
        .attr('cursor', (d: any) => (d._children || d.children) ? 'pointer' : 'default')
        .attr('pointer-events', (d: any) => (d._children || d.children) ? 'all' : 'none');
      bindExpandClick(treeGroup.select('.expandHitArea'));

      treeGroup.select('.expandCircle')
        .attr('cy', 8)
        .attr('r', (d: any) => (d._children || d.children) ? 8 : 0)
        .attr('fill', '#E8EAEE')
        .attr('cursor', (d: any) => (d._children || d.children) ? 'pointer' : 'default')
        .attr('pointer-events', (d: any) => (d._children || d.children) ? 'none' : 'none');
      bindExpandClick(treeGroup.select('.expandCircle'));

      treeGroup.select('.expandLabel')
        .attr('font-family','Poppins')
        .attr('x', (d: any) => d._children === undefined ? 0 : (d.children === null ? 0 : 0.5))
        .attr('y', (d: any) => d._children === undefined ? 0 : (d.children === null ? (isCompact ? 10 : 12) : (isCompact ? 13 : 16)))
        .attr('font-size', (d: any) => d._children === undefined ? '' : (d.children === null ? expandCollapsedSize : expandExpandedSize))
        .attr('font-weight',500)
        .attr('text-anchor','middle')
        .attr('fill',(d: any) => d._children === undefined ? '' : (d.children === null ? '#1363DF' : '#8A98AB'))
        .attr('cursor', (d: any) => (d._children || d.children) ? 'pointer' : 'default')
        .attr('pointer-events', (d: any) => (d._children || d.children) ? 'all' : 'none')
        .text((d: any) => d._children === undefined ? '' : (d.children === null ? '+' : '-'));
      bindExpandClick(treeGroup.select('.expandLabel'));

      treeGroup.select('.nodeLabel')
        .attr('font-family','Poppins')
        .attr('x', (d: any) => d._children === undefined ? 10 : 18)
        .attr('y', (d: any) => 8 + (d.depth > 2 ? (fontSizes["above"] * 0.4) : (fontSizes[d.depth] * 0.4)))
        .attr('font-size', (d: any) => d.depth > 2 ? fontSizes["above"] : fontSizes[d.depth])
        .attr('font-weight',500)
        .text((d: any) => d.data.name)
        .on("mouseover", (event:any, d: any) => {
          if(d.data.name.includes("..")){
            myChart.showTooltip('groupFullName', d.data.fullName, event.offsetX, event.offsetY, width, event);
          }
        })
        .on("mouseout", () => {
          myChart.hideTooltip("groupFullName");
        });;

      mySvg.select('#' + brushClipRectId)
        .style('width', width - margins.left - margins.right - margins.leftExtra + 6)
        .style('height', chartHeight + margins.top + margins.mid + margins.brush + margins.bottom + 10)
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra-1) + ',-10)');

      mySvg.select("#chartGroup" + myClass).attr('clip-path', 'url(#' + brushClipId + ')');

      treeData.map((m: any) => m.rectWidth = Math.max(xScaleSelected(m.dateExtent[1]) - xScaleSelected(m.dateExtent[0]), dayWidth))

      // dates header group
      const treeChartGroup = mySvg
        .select('#chartGroup' + myClass)
        .selectAll('.treeChartGroup' + myClass)
        .data(treeData)
        .join(function(group: any): any {
          const enter = group.append('g').attr('class', 'treeChartGroup' + myClass);
          enter.append('path').attr('class', 'timeRect');
          enter.append('g').attr('class', 'flagGroup');
          enter.append('g').attr('class', 'refillGroup');
          enter.append('rect').attr('class', 'recordGenerateRect');
          enter.append('text').attr('class', 'recordGenerateLabel');
          return enter;
        });

      treeChartGroup.select('.recordGenerateRect')
        .attr("width",(d: any) => d.data.record_generate_type === undefined || d.data.record_generate_type === null ? 0
          : measureWidth(d.data.record_generate_type, recordGenMeasureSize) + 2)
        .attr("height", timeRectHeight -4)
        // .attr('transform', (d: any) => 'translate(' + (xScaleSelected(d.dateExtent[0]) + 7)
        //   + ',' + (2) + ')')
        .attr('transform', (d: any) => {
          const xPos = xScaleSelected(d.dateExtent[0]) + 20;
          const chartRectWidth = width - margins.left - margins.right - margins.leftExtra;
          const shouldFlip = xPos + d.rectWidth > chartRectWidth; 
          const xTrans = shouldFlip ? (xPos - (d.rectWidth + 30)) : xPos - 13;
          return 'translate(' + xTrans + ', 2)'
        })
        .attr("rx", 4)
        .attr("ry", 5)
        .attr('fill',"white")
        .style('filter',  'url(#' + dropShadowId + ')')
        .on('mouseover', (event: any, d: any) => {
          if(d.data.record_generate_type !== undefined && d.data.record_generate_type !== null ){
            myChart.showTooltip('RGT',d.record_generate_type_description, event.offsetX, event.offsetY,width,event);
          }
        })
        .on("mouseout", () => {
          myChart.hideTooltip('RGT')
        })


      treeChartGroup.select('.recordGenerateLabel')
        .attr("pointer-events", "none")
        .attr("height", timeRectHeight -4)
        // .attr('transform', (d: any) => 'translate(' + (xScaleSelected(d.dateExtent[0]) + 10)
        //   + ',' + ((timeRectHeight/2)  + 4) + ')')

        .attr('transform', (d: any) => {
          const xPos = xScaleSelected(d.dateExtent[0]) + 20;
          const chartRectWidth = width - margins.left - margins.right - margins.leftExtra;
          const shouldFlip = xPos + d.rectWidth > chartRectWidth; 
          const xTrans = shouldFlip ? (xPos - (d.rectWidth + 25)) : xPos - 10;
          return 'translate(' + xTrans + ',' + ((timeRectHeight/2)  + 4) +')'
        })
        .attr('fill','#1363DF')
        .attr('font-size', smallLabelSize)
        .attr('font-weight',500)
        .attr('font-family','Poppins')
        .text((d: any) => d.data.record_generate_type === undefined || d.data.record_generate_type === null ? "" : d.data.record_generate_type)

      treeChartGroup.attr('transform', (d: any, i: any) => 'translate(' + (margins.left + margins.leftExtra) + ',' + (margins.top + (i * rowHeight)) + ')');

      treeChartGroup.select('.timeRect')
        .attr('transform', (d: any) => 'translate(' + xScaleSelected(d.dateExtent[0]) + ',0)')
        .attr('stroke', (d: any) => d.colors)
        .attr('fill', (d: any) => d.colors)
        .attr('fill-opacity', (d: any) => d.data.children === undefined ? 1 : (d.children === null ? 1 : 0.4))
        .attr('stroke-opacity', (d: any) => d.data.children === undefined ? 1 : (d.children === null ? 1 : 0.4))
        .attr('stroke-width', 1.5)
        .attr('d', getRectPath)
        .on('mousemove', function(event: any, d: any){
          //temp cheat for demo on Friday
          d3.select('#tempd3Tooltip')
            .style('visibility', 'visible')
            .style('border', '0px solid #707070')
            .style('background-color', '#FFFFFF')
            .style('border-radius', '4px')
            .style('position', 'absolute')
            .style('pointer-events', 'none')
            .style('width', 'auto')
            .style('padding', '4px')
            .style('font-size', smallLabelSize + 'px')
            .style('left', (event.pageX + 10) + 'px')
            .style('top', event.pageY + 'px')
            .style('height', 'auto')
            .html('<strong>' + d.data.name + '</strong><br>From: ' + dateFormat(d.data.startDate)  +  '<br>To: ' + dateFormat(d.data.endDate))
          //@ts-ignore
          d3.select(this).attr('fill-opacity', 0.2);

        })
        .on('mouseout', function(){
          d3.select('#tempd3Tooltip').style('visibility', 'hidden')
          mySvg.selectAll('.timeRect')     .attr('fill-opacity', (d: any) => d.data.children === undefined ? 1 : (d.children === null ? 1 : 0.4));
          myChart.hideTooltip('timeRect');
        })
        .on('click', function(event: any, d: any){
          if(d.data.transactionname === undefined){
           myChart.showTooltip('timeRect',d, event.offsetX, event.offsetY,width,event);
          } else {
           myChart.showTooltip('timeRectLeaf',d, event.offsetX, event.offsetY,width,event);
          }
        })

      const flagMinWidth = 6 + measureWidth(d3.timeFormat("%d %b, %Y")(new Date()), measureLabelSize)
      // dates header group
      const flagGroup = treeChartGroup.select('.flagGroup')
        .selectAll('.flagGroup' + myClass)
        .data((d: any) => {
          //get flags related to my root
          let myFlags: any = [];
          if(d._children !== null){
            //get all flags for this root
            myFlags = flagData.filter((f: any) => d.childIds.indexOf(f.parentId) > -1);
            myFlags = myFlags.sort((a: any, b: any) => d3.descending(a.TransactionDate, b.TransactionDate))
            const flagGroup:any = Array.from(d3.group(myFlags, (g: any) => g.TransactionDate));
            myFlags.map((m: any) => m.flagDateCount = flagGroup.find((f: any) => String(f[0]) === String(m.TransactionDate))[1].length)
            myFlags.map((m: any) => m.flagDateIndex = flagGroup.find((f: any) => String(f[0]) === String(m.TransactionDate))[1].findIndex((a: any) => a.flagId === m.flagId))
            myFlags.map((m: any) => m.yPos = d.yPos);
            myFlags.map((m: any) => m.legendId = m[myChart.props.transactionTypeVar].replace(/ /g, '').toLowerCase())
            myFlags.map((m: any) => m.legendId = m[myChart.props.transactionTypeVar].toLowerCase().includes("change") ? "change" : m.legendId)
          }
          myFlags = myFlags.reverse();
          return myFlags;
      })
        .join(function(group: any): any {
          const enter = group.append('g').attr('class', 'flagGroup' + myClass);
          enter.append('line').attr('class', 'flagDateLine');
          enter.append('path').attr('class', 'flagLinePath');
          enter.append('path').attr('class', 'flagRect');
          enter.append('circle').attr('class', 'flagCircle');
          enter.append('svg').attr('class', 'flagIconSvg').append('path').attr('class', 'flagIconPath');
          enter.append('text').attr('class', 'flagLabel');
          enter.append('text').attr('class', 'flagDateLabel');
          enter.append('text').attr('class', 'flagRefillLabel');
          return enter;
        });

      flagGroup
        .attr("id", (d: any) => d.flagId)
        .attr('class', (d: any) => 'flagGroup' + myClass + ' legendFlagGroup ' + d.legendId)

      d3.selectAll('.flagGroup' + myClass)
        .attr('opacity', (d: any) => myChart.currentLegendSelections.indexOf(d.legendId) > - 1 ? 1 : 0);

      flagGroup.select('.flagLinePath')
        .attr("id", (d: any) => d.flagId)
        .attr('stroke','#8A98AB')
        .attr('fill','transparent')
        .attr('stroke-width', 1)
        .attr('d', 'M2,0 Q0,0 0,2 L0, 11')
        .attr('transform', (d: any) => 'translate(' + xScaleSelected(d.TransactionDate) +  ',' + ( -11.5 + (-20 * d.flagDateIndex)) + ')')

      flagGroup.select('.flagDateLine')
        .attr('visibility', 'hidden')
        .attr("id", (d: any) => d.flagId)
        .attr('stroke',(d: any) => d.refillTooSoon !== null ? '#D11044': '#101D42')
        .attr('stroke-dasharray', '4,2')
        .attr('stroke-width', 1)
        .attr('x1', (d: any) => xScaleSelected(d.TransactionDate))
        .attr('x2', (d: any) => xScaleSelected(d.TransactionDate))
        .attr('y1', (d: any) =>   (-20 * d.flagDateIndex))
        .attr('y2', (d: any) =>   margins.top + chartHeight - d.yPos)

      flagGroup.select( '.flagIconSvg')
        .attr('pointer-events', 'none')
        .attr("visibility", (d: any) => d.name.toLowerCase().includes("change") ? "visible" : "hidden")
        .attr('x',  (d: any) => xScaleSelected(d.TransactionDate) + 7)
        .attr('y', (d: any) => - 17 + (-20 * d.flagDateIndex))
        .attr('viewBox', changeIconViewBox)
        .attr( 'width', 10)
        .attr( 'height', 10)

      flagGroup.select( '.flagIconPath')
        .attr('pointer-events', 'none')
        .attr('fill',  '#1363DF')
        .attr( 'd',  changeIconPath);

        const defs = mySvg.select('defs');

      flagGroup.select('.flagCircle')
        .attr("visibility", (d: any) => d.name.toLowerCase().includes("change") ? "hidden" : "visible")
        .attr("pointer-events", "none")
        .attr('cx', (d: any) => xScaleSelected(d.TransactionDate) + 12)
        .attr('cy', (d: any) =>  - 12 + (-20 * d.flagDateIndex))
        .attr('fill','white')
        .attr('stroke-width', 2.5)
        .attr('stroke', (d: any) => flagColours[d[myChart.props.transactionTypeVar].toLowerCase()])
        .attr('r', 4)
        .each(function (d: any) {
                const color = flagColours[d[myChart.props.transactionTypeVar].toLowerCase()];
                const filterId = `drop-shadow-${d.flagId}`; // Unique filter ID
        
                if (defs.select(`#${filterId}`).empty()) {
                    const filter = defs.append('filter')
                        .attr('id', filterId)
                        .attr('x', '-50%')
                        .attr('y', '-50%')
                        .attr('width', '200%')
                        .attr('height', '200%');
        
                    filter.append('feGaussianBlur')
                        .attr('in', 'SourceAlpha')
                        .attr('stdDeviation', 1.2)
                        .attr('result', 'blur');
        
                    filter.append('feOffset')
                        .attr('in', 'blur')
                        .attr('dx', 1)
                        .attr('dy', 1)
                        .attr('result', 'offsetBlur');
        
                    filter.append('feFlood')
                        .attr('flood-color', color) // Dynamic color
                        .attr('flood-opacity', 0.5) // Adjust as needed
                        .attr('result', 'offsetColor');
        
                    filter.append('feComposite')
                        .attr('in', 'offsetColor')
                        .attr('in2', 'offsetBlur')
                        .attr('operator', 'in')
                        .attr('result', 'offsetBlurWithColor');
        
                    const feMerge = filter.append('feMerge');
                    feMerge.append('feMergeNode').attr('in', 'offsetBlurWithColor');
                    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
                }
            })
            .style('filter', (d: any) => `url(#drop-shadow-${d.flagId})`);
        

      flagGroup.select('.flagLabel')
        .attr("id", (d: any) => d.flagId)
        .attr("pointer-events", "none")
        .attr("visibility", "hidden")
        .attr('x', (d: any) => xScaleSelected(d.TransactionDate) + 20)
        .attr('y', (d: any) =>  - 9 + (-20 * d.flagDateIndex))
        .attr('fill','#101D42')
        .attr('font-size', smallLabelSize)
        .attr('font-weight',500)
        .attr('font-family','Poppins')
        .style('text-transform','capitalize')
        .text((d: any) => d.name.replace(/_/g,' '));

      flagGroup.select('.flagRefillLabel')
        .attr("id", (d: any) => d.flagId)
        .attr("pointer-events", "none")
        .attr("visibility", "hidden")
        .attr('font-family','Poppins')
        .attr('x', (d: any) => xScaleSelected(d.TransactionDate) + 5)
        .attr('y', (d: any) =>   15 + (-20 * d.flagDateIndex))
        .attr('fill','#D11044')
        .attr('font-size', smallLabelSize)
        .attr('font-weight',500)
        .text((d: any) => d.refillTooSoon === null ? '': 'Refill Too Soon')

      flagGroup.select('.flagDateLabel')
        .attr("pointer-events", "none")
        .attr("visibility", "hidden")
        .attr('font-family','Poppins')
        .attr("id", (d: any) => d.flagId)
        .attr('x', (d: any) => xScaleSelected(d.TransactionDate) + 5)
        .attr('y', (d: any) =>  3 + (-20 * d.flagDateIndex))
        .attr('fill','#8A98AB')
        .attr('font-size', smallLabelSize)
        .attr('font-weight',500)
        .text((d: any) => d3.timeFormat("%d %b, %Y")(d.TransactionDate))

        flagGroup.select('.flagRect')
    .attr("id", (d: any) => d.flagId)
    .attr('d', (d: any) => generateFlagPath(d, 20, 16)) // Initial width and height
    .attr('stroke', (d: any) => d.refillTooSoon !== null ? '#D11044' : (d.name.toLowerCase().includes("change")
        ? '#1363DF' : flagColours[d[myChart.props.transactionTypeVar].toLowerCase()]))
    .attr('stroke-width', (d: any) => d.refillTooSoon !== null ? 1 : 0)
    .attr('fill', "white")
    .style('filter', 'url(#' + dropShadowId + ')')
    .on('mouseover', function (event: any, d: any) {
        scopeAll('.flagDateLabel').interrupt().attr('visibility', 'hidden');
        scopeAll('.flagDateLine').interrupt().attr('visibility', 'hidden');
        scopeAll('.flagRect').interrupt()
            .attr('d', (d: any) => generateFlagPath(d, 20, 16)) // Reset path on others
            .attr('stroke-width', (d: any) => d.refillTooSoon !== null ? 0.5 : 0);

        scopeAll('.flagLinePath').interrupt().attr('stroke', '#8A98AB').attr('stroke-dasharray', '');

        //@ts-ignore
        const myObject: any = this;
        d3.select(myObject.parentElement).interrupt().raise().raise();

        const expandedWidth = Math.max(
            21 + measureWidth(d.name, measureSmallSize),
            flagMinWidth,
            (d.refillTooSoon !== null ? 3 + measureWidth('Refill Too Soon', measureSmallSize) : 0)
        );
        const expandedHeight = 28 + (d.refillTooSoon !== null ? 12 : 0);

      // Determine if the flag should flip
      const shouldFlip = event.clientX + expandedWidth > (width - margins.left - margins.right - margins.leftExtra);
      d3.select(myObject)
        .interrupt()
        .transition()
        .duration(100)
        .attr('d', generateFlagPath(d, expandedWidth, expandedHeight, shouldFlip))
        .attr('stroke-width', 1);

        
      if (shouldFlip) {
        let x = xScaleSelected(d.TransactionDate)+2;
        x -= expandedWidth - 25;
        d3.selectAll("text#" + d.flagId).interrupt().transition().delay(100).duration(100).attr('visibility', 'visible').attr("x", x);
      } else {
        d3.selectAll("text#" + d.flagId).interrupt().transition().delay(100).duration(100).attr('visibility', 'visible')
      }


      d3.select("line#" + d.flagId).interrupt().transition().delay(100).duration(100).attr('visibility', 'visible');
      d3.select("path#" + d.flagId).interrupt().attr('stroke', (d: any) => d.refillTooSoon !== null ? '#D11044' : '#101D42').attr('stroke-dasharray', '4,2');
    })
    .on('mouseout', function () {
        myChart.hideTooltip('flag');
        scopeAll('.flagRect').interrupt()
            .transition()
            .duration(100)
            .attr('d', (d: any) => generateFlagPath(d, 20, 16))
            .attr('stroke-width', (d: any) => d.refillTooSoon !== null ? 1 : 0);

        scopeAll('.flagDateLabel').interrupt().attr('visibility', 'hidden');
        scopeAll('.flagLabel').interrupt().attr('visibility', 'hidden');
        scopeAll('.flagRefillLabel').interrupt().attr('visibility', 'hidden');
        scopeAll('.flagDateLine').interrupt().attr('visibility', 'hidden');
        scopeAll('.flagLinePath').interrupt().attr('stroke', '#8A98AB').attr('stroke-dasharray', '');
    })
    .on('click', (event: any, d: any) => {
      if(myChart.props.trans_popup) {
        myChart.showTooltip('flag', d, event.offsetX, event.offsetY, width,event);
      }
    })
    .text((d: any) => d.Description);
    
    // Helper function to generate the path with rounded corners
    function generateFlagPath(d: any, width: number, height: number, flip: boolean = false) {
      let x = xScaleSelected(d.TransactionDate) + 2;
      const y = -20 + (-20 * d.flagDateIndex);
  
      let rTopLeft = 6;
      let rTopRight = 6;
      let rBottomRight = 6;
      let rBottomLeft = 0;

      if (flip) {
        x -= width - 20; // Move the flag left if it’s too close to the right edge
        rBottomRight = 0;
        rBottomLeft = 6;
      }
  

      return `
          M${x + rTopLeft},${y}
          h${width - rTopLeft - rTopRight}
          a${rTopRight},${rTopRight} 0 0 1 ${rTopRight},${rTopRight}
          v${height - rTopRight - rBottomRight}
          a${rBottomRight},${rBottomRight} 0 0 1 -${rBottomRight},${rBottomRight}
          h-${width - rBottomRight - rBottomLeft}
          a${rBottomLeft},${rBottomLeft} 0 0 1 -${rBottomLeft},-${rBottomLeft}
          v-${height - rBottomLeft - rTopLeft}
          a${rTopLeft},${rTopLeft} 0 0 1 ${rTopLeft},-${rTopLeft}
          Z
      `;
    }

      d3.select('#xAxisChart' + myClass)
        //@ts-ignore
        .call(d3.axisTop(xScaleSelected).tickSizeOuter(0).tickValues(xScaleSelected.ticks(7)))
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra) + ',' + (margins.top) + ')');

      d3.selectAll('#xAxisChart' + myClass + ' path')
        .style('display', 'none');

      d3.selectAll('#xAxisChart' + myClass + ' text')
        .text('');

      d3.selectAll('#xAxisChart' + myClass + ' line')
        .attr('y1', '-25')
        .attr('y2', chartHeight)
        .style('stroke', '#E4E4E4')
        .style('stroke-width', 1);

      d3.select('#xAxisChartLabels' + myClass)
        //@ts-ignore
        .call(d3.axisTop(xScaleSelected).tickSizeOuter(0).tickValues(xScaleSelected.ticks(7)))
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra) + ',' + (margins.top) + ')');

      d3.selectAll('#xAxisChartLabels' + myClass + ' path')
        .style('display', 'none');

      d3.selectAll('#xAxisChartLabels' + myClass + ' line')
        .style('display', 'none');

      d3.selectAll('#xAxisChartLabels' + myClass + ' text')
        .style('font-weight', isCompact ? 600 : 700)
        .style('font-family', 'Poppins')
        .style('font-size', axisLabelSize)
        .style('text-anchor', isPortrait ? 'start' : 'middle')
        .attr('y', isPortrait ? -20 : -30)
        .attr('x', isPortrait ? 20 : null)
        .attr('transform', isPortrait ? 'rotate(-45)' : null)
        .text(function (d: any) {
          // @ts-ignore
          const myText = d3.select(this).text()
          // console.log(myText, d)
          return months.indexOf(myText) > -1 ? myText.substr(0,3) + ' ' + d3.timeFormat('%Y')(d)  : myText;
        });

      d3.select('#xAxis' + myClass)
        //@ts-ignore
        .call(d3.axisTop(xScaleBrush).tickSizeOuter(0).ticks(7))
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra) + ',' + (margins.top + chartHeight + margins.mid) + ')');

      d3.selectAll('#xAxis' + myClass + ' path')
        .style('display', 'none');

      d3.selectAll('#xAxis' + myClass + ' text')
        .style('font-family', 'Poppins')
        .style('font-weight', isCompact ? 600 : 700)
        .style('font-size', axisLabelSize)
        .style('text-anchor', isPortrait ? 'end' : 'middle')
        .attr('transform', isPortrait ? 'rotate(-45)' : null)
        .attr('y', isPortrait ? 30 : 12 + margins.brush)
        .attr('x', isPortrait ? -26 : null)
        .text(function (d: any) {
            // @ts-ignore
            const myText = d3.select(this).text();
            return months.indexOf(myText) > -1 ? myText.substr(0,3) + ' ' + d3.timeFormat('%Y')(d) : myText;
          });

      d3.selectAll('#xAxis' + myClass + ' line')
        .attr('y1', '0')
        .attr('y2', margins.brush * 0.75)
        .style('stroke', '#E4E4E4')
        .style('stroke-width', 1);

      d3.select('#brushGroup' + myClass)
        .attr('transform', 'translate(' + (margins.left + margins.leftExtra) + ',' + (margins.top + chartHeight + margins.mid) + ')')

      const handleLineY1 = margins.top + chartHeight + margins.mid + (margins.brush - 12) / 2;
      const handleLineY2 = margins.top + chartHeight + margins.mid + 12 + (margins.brush - 12) / 2;
      const handleLineTransform = 'translate(' + (margins.left + margins.leftExtra) + ',0)';
      if (myChart.embeddedMode) {
        mySvg.selectAll('.handleLeftLine1' + myClass)
          .attr('y1', handleLineY1).attr('y2', handleLineY2)
          .style('stroke', '#8A98AB').style('stroke-width', '1')
          .attr('transform', handleLineTransform);
        mySvg.selectAll('.handleLeftLine2' + myClass)
          .attr('y1', handleLineY1).attr('y2', handleLineY2)
          .style('stroke', '#8A98AB').style('stroke-width', '1')
          .attr('transform', handleLineTransform);
        mySvg.selectAll('.handleRightLine1' + myClass)
          .attr('y1', handleLineY1).attr('y2', handleLineY2)
          .style('stroke', '#8A98AB').style('stroke-width', '1')
          .attr('transform', handleLineTransform);
        mySvg.selectAll('.handleRightLine2' + myClass)
          .attr('y1', handleLineY1).attr('y2', handleLineY2)
          .style('stroke', '#8A98AB').style('stroke-width', '1')
          .attr('transform', handleLineTransform);
      } else {
        d3.selectAll('.handleLines')
          .attr('y1', handleLineY1)
          .attr('y2', handleLineY2)
          .style('stroke', '#8A98AB')
          .style('stroke-width', '1')
          .attr('transform', handleLineTransform);
      }


      const markerPath = "M0 6.10083V1C0 0.447715 0.447715 0 1 0H6C6.55228 0 7 0.447716 7 1V6.10083C7 6.43274 6.83532 6.74302 6.56044 6.92903L4.06044 8.62076C3.72192 8.84983 3.27808 8.84983 2.93957 8.62076L0.439565 6.92903C0.164681 6.74302 0 6.43274 0 6.10083Z"

      // dates header group
      const hasBrands = programData.some((d: any) => d.brand && d.brands && d.brands?.length > 0);

      const headerProgramGroup = mySvg
        .selectAll('.headerProgram' + myClass)
        .data(programData)
        .join(function(group: any): any {
          const enter = group.append('g').attr('class', 'headerProgram' + myClass);
          
          if(hasBrands) {
            enter.append('g').attr('class', 'programRectopacity');
          } else {
            enter.append('text').attr('class', 'programLabel');
          }

          enter.append('rect').attr('class', 'programRect');
          enter.append('line').attr('class', 'markerLineStart');
          enter.append('path').attr('class', 'markerStart');
          enter.append('line').attr('class', 'markerLineEnd');
          enter.append('path').attr('class', 'markerEnd');
          return enter;
        });

      headerProgramGroup.style('display', myChart.props.headerAccess ? 'block' : 'none');

      headerProgramGroup
        .attr('transform', 'translate(' + margins.headerLeft + ',0)')
        .on('mouseover',  (event: any, d: any) => {
          let hoverData = event.toElement.__data__;
          const hasBrand = hoverData?.brand && d[myChart.props.programVar] !== 'Gap';
          const tooltipText = hasBrand
            ? `${d[myChart.props.programVar]}<br>${d.days} days , ${hoverData.brand}<br>${hoverData.days} days`
            : `${d[myChart.props.programVar]}<br>${d.days} days`;

          // let hoverData= event.toElement.__data__
          // const tooltipText = d[myChart.props.programVar] + '<br>' + d.days + ' days' +(d[myChart.props.programVar]=='Gap'?'': ' , ' + hoverData.brand + '<br>' + hoverData.days + ' days') ;
          myChart.showTooltip('program', tooltipText, event.offsetX, event.offsetY, width, event);
        })
        .on('mouseout',  () => {
          myChart.hideTooltip('averageDot');
        });

      headerProgramGroup.select('.markerLineEnd')
        .attr('stroke', '#101D42')
        .attr('x1', (d: any) => xScaleHeader(d[myChart.props.endDateVar]))
        .attr('x2', (d: any) => xScaleHeader(d[myChart.props.endDateVar]))
        .attr('y1', (margins.headerHeight/2) - 14)
        .attr('y2', (d: any) => d.includeEnd === true ? (margins.headerHeight/2) - 8 : (margins.headerHeight/2) - 14);

      headerProgramGroup.select('.markerEnd')
        .attr('fill', '#101D42')
        .attr('d', (d: any) => d.includeEnd === true ? markerPath : '')
        .attr('transform', (d: any) => 'translate(' + (xScaleHeader(d[myChart.props.endDateVar]) - 3.5) +
          ',' +  ((margins.headerHeight/2) - 4 - 19) + ')');

      headerProgramGroup.select('.markerLineStart')
        .attr('stroke', '#101D42')
        .attr('x1', (d: any) => xScaleHeader(d[myChart.props.startDateVar]))
        .attr('x2', (d: any) => xScaleHeader(d[myChart.props.startDateVar]))
        .attr('y1', (margins.headerHeight/2) - 14)
        .attr('y2', (margins.headerHeight/2) - 8);

      headerProgramGroup.select('.markerStart')
        .attr('fill', '#101D42')
        .attr('d', markerPath)
        .attr('transform', (d: any) => 'translate(' + (xScaleHeader(d[myChart.props.startDateVar]) - 3.5) +
          ',' +  ((margins.headerHeight/2) - 4 - 19) + ')');

       headerProgramGroup.select('.programRect')
        .attr('fill', (d: any) => programColourScale(d[myChart.props.programVar]))
        .attr('height', 8)
        .attr('rx', 4)
        .attr('ry', 4)
        .attr('x', (d: any) => xScaleHeader(d[myChart.props.startDateVar]))
        .attr('width', (d: any) => xScaleHeader(d[myChart.props.endDateVar]) - xScaleHeader(d[myChart.props.startDateVar]))
        .attr('y', (margins.headerHeight/2) - 4)
 
      if(hasBrands) {
        const subrange = headerProgramGroup.select('.programRectopacity')
          .selectAll('.programRectopacity' + myClass)
          .data((d: any) => {
            if (d[myChart.props.programVar] === "Gap") return [];
            return d.brands || [];
          })
          .join((group: any) => {
            const enter = group.append('g').attr('class', 'programRectopacity' + myClass);
            enter.append('rect').attr('class', 'programRectopacityBrand');
            enter.append('text').attr('class', 'programRectopacityBrandName');
            return enter;
          })
  
        subrange.select('.programRectopacityBrand')
          .attr('fill', (d: any) => {
            const brand = d.brand;
            const brandColor = myChart.props?.brandColorConfig || [];
            const fallback = myChart.embeddedMode && brand != null
              ? myChart.paletteColorFor('brand:' + brand) : '';
            if (brandColor?.length > 0) {
              const fnd = brandColor.find((f: any) => f.col_value.toString().toLowerCase() === brand.toString().toLowerCase());
              return fnd ? fnd.color : fallback;
            }
            return fallback;
          })
          .attr('opacity', 0.2)
          .attr('height', 30)
          .attr('x', (d: any) => xScaleHeader(d.start_dt))
          .attr('width', (d: any) => xScaleHeader(d.end_dt) - xScaleHeader(d.start_dt))
          .attr('y', (margins.headerHeight / 2) - 15);
  
        subrange.select('.programRectopacityBrandName')
          .attr('fill', '#101D42')
          .style('font-weight', 500)
          .style('font-family', 'Poppins')
          .style('font-size', smallLabelSize)
          .style('text-anchor', 'middle')
          .attr('x', (d: any) => xScaleHeader(d.start_dt) + (xScaleHeader(d.end_dt) - xScaleHeader(d.start_dt))/2)
          .attr('y', (margins.headerHeight/2) - 20)
          .text((d: any) => (measureWidth(d.brand + ': ' + d.days + ' days', measureLabelSize) + 5) > (xScaleHeader(d.end_dt) - xScaleHeader(d.start_dt))
            ? '' : d.brand + ': ' + d.days + ' days');
      } else {
        headerProgramGroup.select('.programLabel')
          .attr('fill', '#101D42')
          .style('font-weight', 500)
          .style('font-family', 'Poppins')
          .style('font-size', smallLabelSize)
          .style('text-anchor', 'middle')
          .attr('x', (d: any) => xScaleHeader(d[myChart.props.startDateVar]) + (xScaleHeader(d[myChart.props.endDateVar]) - xScaleHeader(d[myChart.props.startDateVar]))/2)
          .attr('y', (margins.headerHeight/2) - 14)
          .text((d: any) => (measureWidth(d[myChart.props.programVar] + ': ' + d.days + ' days', measureLabelSize) + 5) > (xScaleHeader(d[myChart.props.endDateVar]) - xScaleHeader(d[myChart.props.startDateVar]))
            ? '' : d[myChart.props.programVar] + ': ' + d.days + ' days');
      }


      // dates header group
      const headerDispenseGroup = mySvg
        .selectAll('.headerDispenseGroup' + myClass)
        .style('display', myChart.props.headerAccess ? 'block' : 'none')
        .data(dispenseData)
        .join(function(group: any): any {
          const enter = group.append('g').attr('class', 'headerDispenseGroup' + myClass);
          enter.append('line').attr('class', 'dispenseCircleLine');
          enter.append('circle').attr('class', 'dispenseCircle');
          return enter;
        });

      headerDispenseGroup
        .attr('visibility', myChart.props.showTopCircleFlags === true ? "visible" : "hidden")
        .attr('class', (d: any) => 'headerDispenseGroup' + myClass + ' legendFlagGroup ' + d.type.replace(/ /g, '').toLowerCase())
        .attr('opacity', (d: any) => myChart.currentLegendSelections.indexOf(d.type.replace(/ /g, '').toLowerCase()) > - 1 ? 1 : 0)
        .attr('transform', 'translate(' + margins.headerLeft + ',0)')

      headerDispenseGroup.select('.dispenseCircle')
        .attr('fill', 'white')
        .attr('stroke', (d: any) => flagColours[d.type.toLowerCase()])
        .attr('r', 4)
        .attr('stroke-width', 2.5)
        .attr('cx', (d: any) => xScaleHeader(d[myChart.props.dispenseTransactionDateVar]))
        .attr('cy', (margins.headerHeight/2) + 4 + 12);

      headerDispenseGroup.select('.dispenseInnerCircle')
        .attr('fill', 'white')
        .attr('r', 1.5)
        .attr('cx', (d: any) => xScaleHeader(d[myChart.props.dispenseTransactionDateVar]))
        .attr('cy', (margins.headerHeight/2) + 4 + 12);

      headerDispenseGroup.select('.dispenseCircleLine')
        .attr('stroke', (d: any) => flagColours[d.type.toLowerCase()])
        .attr('x1', (d: any) => xScaleHeader(d[myChart.props.dispenseTransactionDateVar]))
        .attr('x2', (d: any) => xScaleHeader(d[myChart.props.dispenseTransactionDateVar]))
        .attr('y1', (margins.headerHeight/2) + 8)
        .attr('y2', (margins.headerHeight/2) + 4 + 9);

      // dates header group
      const kpiGroupsGroup = mySvg
        .selectAll('.kpiGroupsGroup' + myClass)
        .style('display', myChart.props.headerAccess ? 'block' : 'none')
        .data(kpiGroups)
        .join(function(group: any): any {
          const enter = group.append('g').attr('class', 'kpiGroupsGroup' + myClass);
          enter.append('g').attr('class', 'kpiGroupRows');
          return enter;
        });

      kpiGroupsGroup.select('.kpiGroupRows')
        .attr('transform', (d: any, i: any ) => 'translate(' + margins.headerLeft + ',' + ((margins.headerHeight/2) + 40 + (i * kpiHeight)) + ')')

      const kpiRowsGroup = kpiGroupsGroup.select('.kpiGroupRows')
        .selectAll('.kpiRowsGroup' + myClass)
        .data((d: any) => d.data)
        .join(function(group: any): any {
          const enter = group.append('g').attr('class', 'kpiRowsGroup' + myClass);
          enter.append('line').attr('class', 'kpiLine');
          enter.append('text').attr('class', 'kpiLabel');
          enter.append('line').attr('class', 'kpiMarkerLineStart');
          enter.append('path').attr('class', 'kpiMarkerStart');
          enter.append('line').attr('class', 'kpiMarkerLineEnd');
          enter.append('path').attr('class', 'kpiMarkerEnd');
          return enter;
        });

      kpiRowsGroup
        .on('mouseover',  (event: any, d: any) => {
          
          const tooltipText = d[myChart.props.kpiVar] + '<br>' + d.dayCount + ' days';
          myChart.showTooltip('program', tooltipText, event.offsetX, event.offsetY, width, event);
        })
        .on('mouseout',  () => {
          myChart.hideTooltip('averageDot');
        });
      kpiRowsGroup.select('.kpiMarkerLineEnd')
        .attr('stroke', '#101D42')
        .attr('x1', (d: any) => xScaleHeader(d[myChart.props.endDateVar]))
        .attr('x2', (d: any) => xScaleHeader(d[myChart.props.endDateVar]))
        .attr('y1',  -8)
        .attr('y2', 0);

      kpiRowsGroup.select('.kpiMarkerEnd')
        .attr('fill', '#101D42')
        .attr('d', markerPath)
        .attr('transform', (d: any) => 'translate(' + (xScaleHeader(d[myChart.props.endDateVar]) + 3.5) +
          ',' +  ( 4) + ')  rotate(180)');

      kpiRowsGroup.select('.kpiMarkerLineStart')
        .attr('stroke', '#101D42')
        .attr('x1', (d: any) => xScaleHeader(d[myChart.props.startDateVar]))
        .attr('x2', (d: any) => xScaleHeader(d[myChart.props.startDateVar]))
        .attr('y1',  -8)
        .attr('y2', 0);

      kpiRowsGroup.select('.kpiMarkerStart')
        .attr('fill', '#101D42')
        .attr('d', markerPath)
        .attr('transform', (d: any) => 'translate(' + (xScaleHeader(d[myChart.props.startDateVar]) + 3.5) +
          ',' +  (4) + ') rotate(180)');

      kpiRowsGroup.select('.kpiLine')
        .attr('stroke', '#E8EAEE')
        .attr('stroke-dasharray', '4,4')
        .attr('x1', (d: any) => xScaleHeader(d[myChart.props.startDateVar]))
        .attr('x2', (d: any) => xScaleHeader(d[myChart.props.endDateVar]))
        .attr('y1', 0)
        .attr('y2', 0);

      kpiRowsGroup.select('.kpiLabel')
        .attr('fill', '#101D42')
        .style('font-weight', 500)
        .style('font-family', 'Poppins')
        .style('font-size', smallLabelSize)
        .style('text-anchor', 'middle')
        .attr('x', (d: any) => xScaleHeader(d[myChart.props.startDateVar]) + (xScaleHeader(d[myChart.props.endDateVar]) - xScaleHeader(d[myChart.props.startDateVar]))/2)
        .attr('y', -5)
        .text((d: any) => (measureWidth(d[myChart.props.kpiVar] + ': ' + d.dayCount + ' days', measureLabelSize) + 5) > (xScaleHeader(d[myChart.props.endDateVar]) - xScaleHeader(d[myChart.props.startDateVar]))
        ? '' : d[myChart.props.kpiVar] + ': ' + d.dayCount + ' days');

      treeLayer.raise();

      if (myChart.embeddedMode) {
        const axisFill = '#101D42';
        d3.selectAll('#xAxisChartLabels' + myClass + ' text').style('fill', axisFill);
        d3.selectAll('#xAxis' + myClass + ' text').style('fill', axisFill);

        const totalHeight = chartHeight + margins.top + margins.bottom + margins.mid + margins.brush;
        if (hostEl) {
          myChart.syncEmbeddedAncestors(hostEl, totalHeight);
          myChart.chartLayoutChanged.emit(totalHeight);
        }
      }

      function getRectPath(d: any){
        if(d.data.children === undefined  || d.rectWidth <= 2){
          return 'M0,0 L' + d.rectWidth + ',0 L' + d.rectWidth +  ' ' + timeRectHeight +  ' L0,' + timeRectHeight +  'Z';
        } else {
          return 'M0,0 L' + d.rectWidth + ',0 L' + d.rectWidth +  ' ' + (timeRectHeight + 3) +
            ' Q' + d.rectWidth + ' ' + (timeRectHeight + 5) + ' , ' + (d.rectWidth-1.5) + ' ' + (timeRectHeight + 2)
            +  ' L' + (d.rectWidth - 2) + ',' + timeRectHeight + 'L2,' + timeRectHeight + ' L1.5,' + (timeRectHeight + 2) +
            ' Q0 ' + (timeRectHeight + 5) + ', 0 ' + (timeRectHeight + 3) + ' Z'
        }
      }
    }

    myChart.embeddedPlotContext = {
      redraw: drawChart,
      hostEl: hostEl!,
    };

    drawChart();
    drawLegend();

    function measureWidth(myText: any, myFontSize: any): any {
      const context: any = document.createElement('canvas').getContext('2d');
      context.font = (myFontSize) + 'px sans-serif';
      return context.measureText(myText).width;
    }
    
    
    
    function brushed(event: any): void {
      const selection = event.selection;
      if (selection !== null) {       
        let [x0, x1] = selection;

        // Convert to date
        let [d0, d1] = [xScaleAll.invert(x0), xScaleAll.invert(x1)];

        // Smallest unit your scale shows (for example: 1 day, 1 hour, etc.)
        const minPixelGap = xScaleAll(d3.timeHour.offset(d0, 1)) - xScaleAll(d0); // for 1 hour
        const minPixelRange = Math.max(minPixelGap, 1); // fallback to 1 pixel minimum

        if ((x1 - x0) < minPixelRange) {
          // Enforce minimum width in pixel space
          x1 = x0 + minPixelRange;

          // Stay within chart width
          const maxX = xScaleAll.range()[1];
          if (x1 > maxX) {
            x0 = maxX - minPixelRange;
            x1 = maxX;
          }

          d3.select('#brushGroup' + myClass).call(brush.move, [x0, x1]);
          return;
        }

        // Set new domain to xScaleSelected
        const xDomain = [xScaleAll.invert(x0), xScaleAll.invert(x1)];
        xScaleSelected.domain(xDomain);

        dayWidth = Math.max(12, xScaleSelected(d3.timeHour.offset(xDomain[0], 1))); // adjust based on smallest unit


        const handleEastX = +mySvg.select('#brushGroup' + myClass).select('.handle--e').attr('x');
        const handleWestX = +mySvg.select('#brushGroup' + myClass).select('.handle--w').attr('x');

        d3.select('.handleLeftLine1' + myClass)
          .attr('x1', handleEastX + 3)
          .attr('x2', handleEastX + 3);

        d3.select('.handleLeftLine2' + myClass)
          .attr('x1', handleEastX + 7)
          .attr('x2', handleEastX + 7);

        d3.select('.handleRightLine1' + myClass)
          .attr('x1', handleWestX + 3)
          .attr('x2', handleWestX + 3);

        d3.select('.handleRightLine2' + myClass)
          .attr('x1', handleWestX + 7)
          .attr('x2', handleWestX + 7);

        d3.select('#brushGroup' + myClass)
          .selectAll('.handle')
          .attr('fill', 'white')
          .attr('rx', 4)
          .attr('ry', 4)
          .attr('y', (margins.brush - 24) / 2)
          .attr('height', 24)
          .style('filter',  'url(#' + dropShadowId + ')');

        if(event.sourceEvent !== undefined){
          drawChart();
        }

      }
    }

    function drawLegend(): void {
      // draw legend
      const legendGroup = mySvg
        .selectAll('.legendGroup' + myClass)
        .data(flagSet)
        .join(function (group: any): any {
          const enter = group.append('g').attr('class', 'legendGroup' + myClass);
          enter.append('circle').attr('class', 'legendItem legendCircle');
          enter.append('text').attr('class', 'legendItem legendLabel');
          return enter;
        });

      legendGroup
        .attr('opacity', (d: any) => myChart.currentLegendSelections.indexOf(d.toLowerCase().replace(/ /g, '')) > -1 ? 1 : 0.4)
        .attr('id', (d: any, i: any) => 'group_legendLabel_' + myClass + '_' + i)
        .on('click', legendClick)

      legendGroup
        .select('.legendCircle')
        .attr('fill-opacity', 1)
        .attr('id', (d: any, i: any) => 'circle_legendLabel_' + myClass + '_' + i)
        .attr('cy', 10)
        .attr('r', 4)
        .attr('fill', 'white')
        .attr('stroke-width', '2.5')
        .attr('stroke', (d: any) => flagColours[d.toLowerCase()])
        .attr('cursor', 'pointer');

      legendGroup
        .select('.legendLabel')
        .attr('fill-opacity', 1)
        .attr('font-size', legendLabelSize)
        .attr('font-weight', 500)
        .attr('font-family', 'Poppins')
        .style('text-transform', 'uppercase')
        .attr('id', (d: any, i: any) => 'legendLabel_' + myClass + '_' + i)
        .attr('y', 10 + 4)
        .attr('fill', '#101D42')
        .attr('cursor', 'pointer')
        .text((d: any) => d?.toUpperCase());

      function legendClick(event: any, d: any) {
        //@ts-ignore
        const myItem: any = this;
        const legendId = d.replace(/ /g, '').toLowerCase();
        const chartFlagGroups = mySvg.selectAll('.legendFlagGroup.' + legendId);
        if (+d3.select(myItem).attr('opacity') === 1) {
          myChart.currentLegendSelections = myChart.currentLegendSelections.filter((f: any) => f !== legendId);
          //@ts-ignore
          mySvg.select('#' + myItem.id).interrupt().transition().duration(200).attr('opacity', 0.4);
          chartFlagGroups.interrupt().transition().duration(200).attr('opacity', 0);
        } else {
          myChart.currentLegendSelections.push(legendId);
          //@ts-ignore
          mySvg.select('#' + myItem.id).interrupt().attr('opacity', 1);
          chartFlagGroups.interrupt().transition().duration(200).attr('opacity', 1);
        }
      }

      let legendX = 15;

      legendGroup.select('.legendLabel').each(function (_d: any, i: number, nodes: ArrayLike<SVGTextElement>): void {
        const myItem = nodes[i];
        const myWidth = myItem.getBoundingClientRect()?.width || 0;
        mySvg.select('#circle_' + myItem.id).attr('cx', legendX);
        d3.select(myItem).attr('x', legendX + 5 + 8);
        legendX += myWidth + 5 * 2 + 25;
      });
      legendX -= 5 * 2;

      legendGroup.attr('transform', 'translate(' + (margins.leftExtra + ((width - margins.leftExtra) - legendX)/2) + ',1)');

      d3.select('#legendBackgroundRect' + myClass)
        .attr('width', legendX + 5)
        .attr('height', 20)
        .attr('rx', 10)
        .attr('ry', 10)
        .attr('fill', 'transparent')
        .attr('stroke', (flagSet?.length>0)?'#E8EAEE':'transparent')
        .attr('stroke-width', '1.5px')
        .attr('transform', 'translate(' + (margins.leftExtra + ((width - margins.leftExtra)  - legendX)/2) + ',1)');
    }

      function formatData(){

        myChart.ganttChartData.map((m: any) => m[(myChart.props.transactionTypeVar).toLowerCase()] === m[(myChart.props.transactionTypeVar).toLowerCase()]);

      const hierarchyLevels = myChart.props.levels;

      const buildHierarchyString = (d: any) => {
        return d[hierarchyLevels[0]] + ":" + d[hierarchyLevels[1]] + ":" + d[hierarchyLevels[2]] + ":" + d[hierarchyLevels[3]] + ":" + d[hierarchyLevels[4]] + ":" + d[hierarchyLevels[5]]
      }
      let levelsSet: any = new Set();
      myChart.ganttChartData.forEach((d: any) => {
        levelsSet.add(buildHierarchyString(d));
      })
      levelsSet = Array.from(levelsSet);
      myChart.ganttChartData.map((m: any) => m.allHierarchyIndex = levelsSet.findIndex((f: any) => f === buildHierarchyString(m)))
    //adding temp hierarchy index so parents will map properly
        //long term solution with Arshad
        //so I am sure there is a more efficient way of doing this without repeating the code but
      //I started by going through it step by step to ensure I was being accurate.
      let hierarchy: any = {"name": "root", "children":[], rootParent: "root", descendantIndex: 0};
      //filter out non-status transactions
      let hierarchyData = myChart.ganttChartData.filter((f: any) => f[myChart.props.transactionTypeVar] === "status");
      const transactionData = myChart.ganttChartData.filter((f: any) => f[myChart.props.transactionTypeVar] !== "status");

      //sort hierarchy data by TransactionDate
      hierarchyData = hierarchyData.sort((a: any, b: any) => d3.ascending(a.allHierarchyIndex, b.allHierarchyIndex) || d3.ascending(a.TransactionDate, b.TransactionDate));
      //calculate the endDate - the first TransactionDate > this one (last will be undefined)
      let currentIndex = 1;
      //get the Level0Set
      let level0Set: any = new Set();

      hierarchyData.forEach((d: any) => level0Set.add(d[hierarchyLevels[0]]));
      level0Set = Array.from(level0Set);
      level0Set.forEach((d: any) => {
        //loop through
        //filter the data for this entry
        let filteredData = hierarchyData.filter((f: any) => f[hierarchyLevels[0]] === d);
        //sort
        filteredData = filteredData.sort((a: any, b: any) => d3.ascending(a.TransactionDate, b.TransactionDate));
        //add to root (calculating startDate + endDate (used when children not expanded)
        const myRoot: any = {
          name: d,
          children: [],
          startDate: d3.min(filteredData, (t: any) => t.TransactionDate),
          endDate: d3.max(filteredData, (t: any) => t.endDate),
          rootParent: d,
          descendantIndex: currentIndex,
          hierarchyPath: String(d),
          type: 'default'
        };
        currentIndex += 1;
        //repeat the process for level1
        let level1Set: any = new Set();
        filteredData.forEach((f: any) => level1Set.add(f[hierarchyLevels[1]]));
        level1Set = Array.from(level1Set).filter((f: any) => f !== null);
        level1Set.forEach((l: any, i: any) => {
          //filtering the level0 data for this level1 entry
          let filteredData1 = filteredData.filter((f: any) => f[hierarchyLevels[1]] === l);
          filteredData1 = filteredData1.sort((a: any, b: any) => d3.ascending(a.TransactionDate, b.TransactionDate));
          myRoot.children.push({
              name: l,
              children: [],
              startDate: d3.min(filteredData1, (t: any) => t.TransactionDate),
              endDate: d3.max(filteredData1, (t: any) => t.endDate),
              rootParent: d,
              descendantIndex: currentIndex,
              hierarchyPath: `${d}|${l}`,
              type: 'default'
          })
          currentIndex += 1;
          //and again for level 2
          let level2Set: any = new Set();
          filteredData1.forEach((f: any) => level2Set.add(f[hierarchyLevels[2]]));
          level2Set = Array.from(level2Set).filter((f: any) => f !== null);
          level2Set.forEach((l2: any, i2: any) => {
            let filteredData2 = filteredData1.filter((f: any) => f[hierarchyLevels[2]] === l2);
            filteredData2 = filteredData2.sort((a: any, b: any) => d3.ascending(a.TransactionDate, b.TransactionDate));
            myRoot.children[i].children.push({
              name: l2,
              children: [],
              startDate: d3.min(filteredData2, (t: any) => t.TransactionDate),
              endDate: d3.max(filteredData2, (t: any) => t.endDate),
              rootParent: d,
              descendantIndex: currentIndex,
              hierarchyPath: `${d}|${l}|${l2}`,
              type: 'default'
            })
            currentIndex += 1;
            //and level 3
            let level3Set: any = new Set();
            filteredData2.forEach((f: any) => level3Set.add(f[hierarchyLevels[3]]));
            level3Set = Array.from(level3Set).filter((f: any) => f !== null);
            level3Set.forEach((l3: any, i3: any) => {
              let filteredData3 = filteredData2.filter((f: any) => f[hierarchyLevels[3]] === l3);
              filteredData3 = filteredData3.sort((a: any, b: any) => d3.ascending(a.TransactionDate, b.TransactionDate));
              myRoot.children[i].children[i2].children.push({
                name: l3,
                children: [],
                startDate: d3.min(filteredData3, (t: any) => t.TransactionDate),
                endDate: d3.max(filteredData3, (t: any) => t.endDate),
                rootParent: d,
                descendantIndex: currentIndex,
                hierarchyPath: `${d}|${l}|${l2}|${l3}`,
                type: 'default'
              })

              currentIndex += 1;
              //then for level 4
              let level4Set: any = new Set();
              filteredData3.forEach((f: any) => level4Set.add(f[hierarchyLevels[4]]));
              level4Set = Array.from(level4Set).filter((f: any) => f !== null);
              level4Set.forEach((l4: any, i4: any) => {
                let filteredData4 = filteredData3.filter((f: any) => f[hierarchyLevels[4]] === l4);
                filteredData4 = filteredData4.sort((a: any, b: any) => d3.ascending(a.TransactionDate, b.TransactionDate));
                if(filteredData4.length > 0 && filteredData4[0][hierarchyLevels[5]] === null){
                  currentIndex += 1;
                  filteredData4.forEach((f: any, fi: any) => {
                    f.name = f[hierarchyLevels[4]]
                    f.startDate = f.TransactionDate
                    f.rootParent = d;
                    f.descendantIndex = currentIndex;
                    f.hierarchyPath = `${d}|${l}|${l2}|${l3}|${f.name}`;
                    f.type = f.record_typ;
                    currentIndex += 1;
                    if(f.name !== null){
                      myRoot.children[i].children[i2].children[i3].children.push(f)
                    }
                  })
                } else {
                  myRoot.children[i].children[i2].children[i3].children.push({
                    name: l4,
                    children: [],
                    startDate: d3.min(filteredData4, (t: any) => t.TransactionDate),
                    endDate: d3.max(filteredData4, (t: any) => t.endDate),
                    rootParent: d,
                    descendantIndex: currentIndex,
                    hierarchyPath: `${d}|${l}|${l2}|${l3}|${l4}`,
                    type: 'default'
                  })
                  currentIndex += 1;
                  //the final level is just filteredData4..
                  //add name and startDate for consistency across hierarchy descendants
                  filteredData4.forEach((f: any, fi: any) => {
                    f.name = f[hierarchyLevels[5]]
                    f.startDate = f.TransactionDate
                    f.rootParent = d;
                    f.descendantIndex = currentIndex;
                    f.hierarchyPath = `${d}|${l}|${l2}|${l3}|${l4}|${f.name}`;
                    f.type = f.record_typ;
                    currentIndex += 1;
                    if(f.name !== null){
                      myRoot.children[i].children[i2].children[i3].children[i4].children.push(f)
                    }
                  })
                }

              })
            })
          })
        })
        hierarchy.children.push(myRoot);
      })
      hierarchy = d3.hierarchy(hierarchy);
      hierarchy.descendants().map((m: any) => m.rootIndex =  m.depth === 1 ? m.data.descendantIndex : hierarchy.descendants().find((f: any) => f.data.name === m.data.rootParent).data.descendantIndex);
      hierarchy.descendants().map((m: any) => m.childIds =  m.descendants().map((c: any) => c.data.descendantIndex));
      hierarchy.descendants().map((m: any) => m.data.duration =  d3.timeDay.count(m.data.startDate, m.data.endDate));
      hierarchy.descendants().map((m: any) => m.colors = m.data.type === "NEXT_FILL"? (m.data.startDate < new Date () ? "#E34071" : "#EAE0FF" )
        : myChart.props.groupColours[m.data.rootParent]  === undefined ? myChart.props.groupColours['default'] : myChart.props.groupColours[m.data.rootParent])

        hierarchy.descendants().forEach((d: any) => {
        let statusCodeSet: any = new Set();
        d.childIds.forEach((c: any) => {
          const myChild = hierarchy.descendants().find((f: any) => f.data.descendantIndex === c);
          statusCodeSet.add(myChild.data.statuscodes)
        })
        d.statuscodes = Array.from(statusCodeSet).filter(f => f !== undefined);
      })
        //now pull out the milestones and transactions and link to descendants index
      const flags: any = [];
      transactionData.forEach((d: any, i: any) => {
        const myParent: any = hierarchy.descendants().find((f: any) => f.data.transactionid === d.parenttransactionid);
        const refillTooSoon: any = myChart.refillData?.find((f: any) => f.second_transaction_id === d.transactionid);
         if(myParent === undefined){
          // console.log('this flag has no matching parent', d)
        } else {
          d.parentDepth = myParent.depth;
          d.parentId = myParent.data.descendantIndex;
          d.rootId = myParent.rootIndex;
          d.name = d.transactionname === null ? d[myChart.props.transactionTypeVar] : d.transactionname
          d.flagId = myChart.embeddedMode ? ('flag_' + myClass + '_' + i) : ('flag' + i)
          d.refillTooSoon = refillTooSoon === undefined ? null : refillTooSoon.first_transaction_id
          flags.push(d)
        }
      })

        flags.forEach((m: any) => m.refillTooSoon = m.refillTooSoon === null ? null : flags.find((f: any) => f.transactionid === m.refillTooSoon).flagId);

      return {chartData: hierarchy, flagData: flags};

    }

  }

  initiateCharts(hostEl?: HTMLElement): void {
    const host = hostEl || this.getChartHost();
    if (!host || host.querySelector('svg')) {
      return;
    }
    const myClass = this.divId;
    const mySvg = d3.select(host)
        .append('svg')
        .attr('id', 'svg_' + myClass)
        .attr('width', '100%')
        .attr('height', 0)
        .style('background-color', 'white');

    //header chart
    mySvg.append('text').attr('class', 'headerTitle' + myClass);
    mySvg.append('text').attr('class', 'headerTitleValue' + myClass);
    mySvg.append('rect').attr('class', 'headerBackgroundRect' + myClass);

    //main chart
    mySvg.append('rect').attr('class', 'chartBackgroundRect' + myClass);
    mySvg.append('rect').attr('class', 'brushBackgroundRect' + myClass);
    mySvg.append('g').attr('id', 'xAxis' + myClass);

    const chartGroup = mySvg.append('g').attr('id', 'chartGroup' + myClass);
    chartGroup.append('g').attr('id', 'xAxisChart' + myClass);
    mySvg.append('g').attr('id', 'xAxisChartLabels' + myClass);
    chartGroup.append('line').attr('class', 'todayLine' + myClass);
    chartGroup.append('path').attr('class', 'todayLineTriangle' + myClass);
    mySvg.append('g').attr('id', 'brushChartGroup' + myClass);

    const brushGroup = mySvg.append('g').attr('id', 'brushGroup' + myClass);

    mySvg.append('line').attr('class', 'handleLines handleLeftLine1' + myClass);
    mySvg.append('line').attr('class', 'handleLines handleLeftLine2' + myClass);
    mySvg.append('line').attr('class', 'handleLines handleRightLine1' + myClass);
    mySvg.append('line').attr('class', 'handleLines handleRightLine2' + myClass);

    const defs = mySvg.append('defs');

    const brushClipId = this.embeddedSvgId('ganttBrushClip');
    const brushClipRectId = this.embeddedSvgId('ganttBrushClipRect');
    const dropShadowId = this.embeddedSvgId('drop-shadow');

    defs.append('clipPath').attr('id', brushClipId)
      .append('rect').attr('id', brushClipRectId);

    const filter = defs.append('filter').attr('id', dropShadowId).attr('width', 10).attr('height', 24);
    filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 1).attr('result', 'blur');
    filter.append('feOffset').attr('in', 'blur').attr('dx', 0).attr('dy', 0).attr('result', 'offsetBlur');
    filter.append('feFlood').attr('in', 'offsetBlur')
      .attr('flood-color', '#000000')
      .attr('flood-opacity', 0.4).attr('result', 'offsetColor');
    filter.append('feComposite').attr('in', 'offsetColor').attr('in2', 'offsetBlur').attr('operator', 'in').attr('result', 'offsetBlur');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    mySvg.append('rect')
      .attr('id', 'legendBackgroundRect' + myClass);

    mySvg.append('g').attr('id', 'treeLayer' + myClass);
  }

  findMonthBetweenDates(data: any) {
    // 
    // Initialize minDate and maxDate
    let minDate = new Date(data[0].transactiondate);
    let maxDate = new Date(); // Always set to the current date (current month)
  
    // Find minDate from the data
    data.forEach((item: any) => {
      const startDate = new Date(item.transactiondate);
  
      if (startDate < minDate) minDate = startDate;
    });
  
    // Function to calculate the difference in months
    function getMonthDifference(startDate: Date, endDate: Date) {
      return (
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth())
      );
    }
  
    // Calculate the difference in months
    const monthDifference = getMonthDifference(minDate, maxDate);
    return monthDifference;
  }

  // Get Chart Data
  getGanttChartData() {
    this.ganttChartData = [];
    
    let api_key = this.widgets?.config?.list_of_api?.["api_key"] || 100104;

    let obj:any = {
      api_key: api_key,
      report_typ: "D",
      client_key: this.clientKey || "",
      patient_id: this.patientId
    }

    this.loader({loader: true, data: this.ganttChartData});
    
    if(Object.keys(this.filterServiceOld.baseQuery).length>0) {
      Object.assign(obj, this.filterServiceOld.baseQuery);
    }
    
    let widget_endpoint =  this.widgets?.config?.list_of_api_endpoint || "PSS/get-pss-data"
    
    this.reqSubcription.push(this.filterService.executeDynamicPatientQuery(widget_endpoint, obj).subscribe((resp: any) => {
      if (resp.length>0) {
        this.applyChartData(resp);
      } else {
        this.ganttChartData = [];
        this.clearChart();
      }
      this.loader({loader: false, data: this.ganttChartData});
    }, err=> {
      this.ganttChartData = [];
      this.clearChart();
      this.loader({loader: false, data: []});
    }))

  }

  /** Load pre-supplied data in embedded mode (Scraii AI). */
  private loadEmbeddedData(): void {
    if (this.externalChartData?.[0]?.patientid) {
      this.patientId = this.externalChartData[0].patientid;
    }
    const showHeader = this.widgets?.config?.['headerAccess']?.name != 'No';
    if (showHeader && this.externalProgramData?.length) {
      this.applyProgramData(this.externalProgramData);
    }
    if (this.externalChartData?.length) {
      const chartUnchanged = this.isSameChartSnapshot(this.externalChartData);
      if (!chartUnchanged) {
        this.loader({ loader: true, data: [] });
        this.applyChartData(this.externalChartData);
        this.loader({ loader: false, data: this.ganttChartData });
      }
    }
  }

  /** Normalise transaction rows and build props for plotChart(). */
  applyChartData(resp: any[]): void {
    if (!resp?.length) {
      this.ganttChartData = [];
      this.treeNodeExpandOverrides.clear();
      this.clearChart();
      return;
    }

    const nextPatientId = this.getEmbeddedPatientId(resp);
    const currentPatientId = this.getEmbeddedPatientId(
      Array.isArray(this.ganttChartData) ? this.ganttChartData : []
    );
    const preserveExpandOverrides =
      this.isSameChartSnapshot(resp) ||
      (this.embeddedMode && !!nextPatientId && nextPatientId === currentPatientId);

    const parsed = resp.map((m: any) => ({
      ...m,
      transactiondate: moment.isMoment(m.transactiondate)
        ? m.transactiondate
        : moment(m.transactiondate, 'MM-DD-YYYY'),
      transactionenddate: moment.isMoment(m.transactionenddate)
        ? m.transactionenddate
        : moment(m.transactionenddate, 'MM-DD-YYYY'),
    }));

    this.ganttChartData = parsed;
    if (!preserveExpandOverrides) {
      this.treeNodeExpandOverrides.clear();
    }
    if (!this.embeddedMode) {
      this.dataService.chartData.next(this.ganttChartData);
    }
    const monthDifference = this.findMonthBetweenDates(this.ganttChartData);

    let treeConfig: any = {};
    if (this.widgets?.config?.tree_color_config?.['values']) {
      this.widgets.config.tree_color_config['values'].forEach((e: any) => {
        treeConfig[e.col_value] = e.color;
      });
    }

    let legendConfig: any = {};
    if (this.widgets?.config?.legend_color_config?.['values']) {
      this.widgets.config.legend_color_config['values'].forEach((e: any) => {
        legendConfig[e.col_value] = e.color;
      });
    }

    let ProgramConfig: any = {};
    if (this.widgets?.config?.program_color_config?.['values']) {
      this.widgets.config.program_color_config['values'].forEach((e: any) => {
        ProgramConfig[e.col_value] = e.color;
      });
    }

    let brandColorConfig: any[] = [];
    if (this.widgets?.config?.brand_color_config?.values) {
      this.widgets.config.brand_color_config.values.forEach((e: any) => {
        brandColorConfig.push({ col_value: e.col_value, color: e.color });
      });
    }

    this.selctedLegendData = Array.from(new Set(
      this.ganttChartData.map((m: any) => m.transactiontype.toLowerCase().replace(/\s+/g, ''))
    ));
    this.totalLegendData = this.selctedLegendData;

    this.props = {
      "colors": Object.keys(legendConfig).length > 0 ? legendConfig : {
        "dispense": "#8C23E6",
        "nurse call": "#E223E6",
        "copay": "#FFCD4A",
        "testnotindata": "purple",
        "change": "#1468E1",
        "free goods": 'red'
      },
      "brandColorConfig": brandColorConfig,
      "programColorMapping": Object.keys(ProgramConfig).length > 0 ? ProgramConfig : {
        "dispense": "#4AA8FF",
        "defualt": "#1468E1",
      },
      "transactionDateVar": "transactiondate",
      "transactionEndDateVar": "transactionenddate",
      "programVar": "program_nm",
      "startDateVar": "start_dt",
      "transactionTypeVar": "transactiontype",
      "endDateVar": "end_dt",
      "dispenseTransactionDateVar": "transaction_dt",
      "kpiVar": "program_nm",
      "headerAccess": this.widgets?.config?.['headerAccess']?.name == 'No' ? false : true,
      "trans_popup": this.widgets?.config?.['trans_popup']?.name == 'No' ? false : true,
      "newLegendSelections": this.selctedLegendData,
      "monthsVisibleStart": monthDifference,
      "showTopCircleFlags": this.showCircleFlags == 'yes' ? true : false,
      "groupColours": Object.keys(treeConfig).length > 0 ? treeConfig : { "HUB": "#ffaf4a", "SP": "#6332CA", "default": "#6332CA" },
      "expandCollapseAll": this.collapse,
      "levels": ["level1", "level2", "level3", "level4", "level5", "level6"],
      "defaultExpend": this.widgets?.config?.['defaultExpend'] || 0
    };
    this.schedulePlotChart();
  }

  /** Normalise program header rows and update headerChartData.program. */
  applyProgramData(resp: any[]): void {
    if (!resp?.length) {
      return;
    }

    const parsed = resp.map((m: any) => ({
      ...m,
      start_dt: moment.isMoment(m.start_dt) ? m.start_dt : moment(m.start_dt, 'MM-DD-YYYY'),
      end_dt: moment.isMoment(m.end_dt) ? m.end_dt : moment(m.end_dt, 'MM-DD-YYYY'),
    }));

    let adjustedDate = this.adjustDates(parsed);
    adjustedDate = this.gapDays(adjustedDate);
    adjustedDate = this.mergeProgramsWithBrandDetails(adjustedDate);
    this.headerChartData.program = adjustedDate;
    if (this.ganttChartData?.length > 0) {
      this.schedulePlotChart();
    }
  }

  // Reset the Gantt SVG back to an empty skeleton, discarding whatever was last
  // drawn. Used when a query returns no data (or errors) so the old chart does
  // not linger in the DOM.
  clearChart(): void {
    const host = this.getChartHost();
    if (host) {
      d3.select(host).selectAll('svg').remove();
      this.initiateCharts(host);
      return;
    }
    d3.select('#svg_' + this.divId).remove();
    this.initiateCharts();
  }

  mergePrograms(programs: any[]): any[] {
    if (!programs || programs.length === 0) {
      return [];
    }
  
    // Sort the array by patient_id, program_nm, and start_dt
    programs.sort((a, b) => 
      a.patient_id - b.patient_id || 
      a.program_nm.localeCompare(b.program_nm) || 
      new Date(a.start_dt).getTime() - new Date(b.start_dt).getTime()
    );
  
    let mergedPrograms: any[] = [];
    let current = { ...programs[0] };
  
    for (let i = 1; i < programs.length; i++) {
      let next = programs[i];
  
      let currentEndDate = new Date(current.end_dt);
      let nextStartDate = new Date(next.start_dt);
      let oneDayAfterCurrent = new Date(currentEndDate);
      oneDayAfterCurrent.setDate(oneDayAfterCurrent.getDate() + 1);
  
      if (
        current.patient_id === next.patient_id &&
        current.program_nm === next.program_nm &&
        (currentEndDate.getTime() === nextStartDate.getTime() || 
         oneDayAfterCurrent.getTime() === nextStartDate.getTime())
      ) {
        // Merge logic
        current.end_dt = next.end_dt;
        current.days += next.days;
      } else {
        // Push the current and reset
        mergedPrograms.push(current);
        current = { ...next };
      }
    }
  
    // Push the last merged program
    mergedPrograms.push(current);
  
    return mergedPrograms;
  }

  adjustDates(data: any) {
    // Sort by start date in ascending order
    data.sort((a: any, b: any) => new Date(a.start_dt).getTime() - new Date(b.start_dt).getTime());
  
    let prevEndDate: Date | null = null;
  
    for (let i = 0; i < data.length; i++) {
        let startDate = new Date(data[i].start_dt);
        let endDate = new Date(data[i].end_dt);
        
        // If the start date overlaps with the previous end date, shift both start & end dates
        if (prevEndDate && startDate <= prevEndDate) {
            let diffDays = Math.ceil((prevEndDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
            startDate.setDate(startDate.getDate() + diffDays); // Move start date forward
            endDate = new Date(startDate); // Reset end date based on new start date
            endDate.setDate(startDate.getDate() + data[i].days); // Adjust end date correctly
        }

        // Fix for the last position
        if (i === data.length - 1) {
            endDate.setDate(startDate.getDate() + data[i].days);
        }

        // Update the object with the new dates (ensure format is "MM-DD-YYYY")
        data[i].start_dt = startDate;
        data[i].end_dt = endDate;

        // Update prevEndDate for the next iteration
        prevEndDate = endDate;
    }
  
    return data;
  }

  gapDays(programs: any[]): any[] {
    let result: any[] = [];
  
    for (let i = 0; i < programs.length - 1; i++) {
      result.push(programs[i]); // Add the program
  
      let currentEndDate = new Date(programs[i].end_dt);
      let nextStartDate = new Date(programs[i + 1].start_dt);
  
      let gap = Math.floor(
        (nextStartDate.getTime() - currentEndDate.getTime()) / (1000 * 60 * 60 * 24)
      );
  
      let gapStart = new Date(currentEndDate);
      gapStart.setDate(gapStart.getDate()); // Move to next day

      let gapEnd = new Date(nextStartDate);
      gapEnd.setDate(gapEnd.getDate()); 

      if (gap > 1) {
        result.push({
          patient_id: programs[i].patient_id,
          program_nm: "Gap",
          start_dt: gapStart,
          end_dt: gapEnd,
          days: gap,
          gapdays: true
        });
      }
    }
  
    // Add the last program
    result.push(programs[programs.length - 1]);
  
    return result;
  }

  // ------API Calls For Header Chart and Popup Data's---------//
  // Transaction Popup
  getTransactionPopupDetails(apiKey: any, report_type: any, id: any) {

    let api_key = this.widgets?.config?.list_of_api_transaction?.["api_key"] || 1001081;

    const obj = {
      api_key: api_key || "",
      report_typ: 'D',// report_type[0].toUpperCase(),
      client_key: this.clientKey || "",
      patient_id: this.patientId,
      transaction_id: id,
      brand_key: this?.filterServiceOld?.baseQuery?.brand_key || 200104,
      tbl_name_suffix: this?.filterServiceOld?.baseQuery?.tbl_name_suffix ?? "rezu",
    }

    if(this.filterServiceOld?.baseQuery && Object.keys(this.filterServiceOld?.baseQuery).length>0) {
      Object.assign(obj, this.filterServiceOld.baseQuery);
    }
    
    let widget_endpoint =  this.widgets?.config?.list_of_api_transaction_endpoint || "PSS/get-transaction-data"

    this.reqSubcription.push(this.filterService.getTransactionData(widget_endpoint, obj).subscribe((resp: any) => {
      if (resp) {
        this.transactionData = (resp)
      }
      if(this.ganttChartData.length>0) {
        // setTimeout(() => {this.plotChart();}, 100);
      }
    }))
  }

  // Programs Data for Header
  getProgramsData() {
    let api_key = this.widgets?.config?.list_of_api_therapy?.["api_key"] || 100108;
    const obj = {
      api_key: api_key,
      report_typ: "D",
      client_key: this.clientKey || "",
      patient_id: this.patientId
    }

    if(Object.keys(this.filterServiceOld.baseQuery).length>0) {
      Object.assign(obj, this.filterServiceOld.baseQuery);
    }

    let widget_endpoint =  this.widgets?.config?.list_of_api_therapy_endpoint || "PSS/get-pss-data";

    // this.reqSubcription.push(this.dataService.getJson('GanttChart-New-Program').subscribe((resp: any) => {
    this.reqSubcription.push(this.filterService.executeDynamicPatientQuery(widget_endpoint, obj).subscribe((resp: any) => {
      if (resp?.length > 0) {
        this.applyProgramData(resp);
      }
    }))
  }

  mergeProgramsWithBrandDetails(programs: any[]): any[] {
    if (!programs || programs.length === 0) return [];

    // Sort input by patient, program, start_dt
    programs.sort((a, b) =>
      a.patient_id - b.patient_id ||
      a.program_nm.localeCompare(b.program_nm) ||
      new Date(a.start_dt).getTime() - new Date(b.start_dt).getTime()
    );

    const mergedPrograms: any[] = [];

    let current = {
      ...programs[0],
      brands: [
        {
          brand: programs[0].brand,
          start_dt: programs[0].start_dt,
          end_dt: programs[0].end_dt,
          days: programs[0].days
        }
      ]
    };

    const mergeBrandSegments = (segments: any[]) => {
      if (!segments.length) return [];
      const result = [segments[0]];

      for (let i = 1; i < segments.length; i++) {
        const last = result[result.length - 1];
        const curr = segments[i];

        const lastEnd = new Date(last.end_dt);
        const currStart = new Date(curr.start_dt);
        const nextDay = new Date(lastEnd);
        nextDay.setDate(nextDay.getDate() + 1);

        if (last.brand === curr.brand && (
          lastEnd.getTime() === currStart.getTime() ||
          nextDay.getTime() === currStart.getTime()
        )) {
          // Merge same-brand segments
          last.end_dt = curr.end_dt;
          last.days += curr.days;
        } else {
          result.push(curr);
        }
      }

      return result;
    };

    for (let i = 1; i < programs.length; i++) {
      const next = programs[i];

      const currEnd = new Date(current.end_dt);
      const nextStart = new Date(next.start_dt);
      const nextDay = new Date(currEnd);
      nextDay.setDate(nextDay.getDate() + 1);

      const isContinuous =
        current.patient_id === next.patient_id &&
        current.program_nm === next.program_nm &&
        (
          currEnd.getTime() === nextStart.getTime() ||
          nextDay.getTime() === nextStart.getTime()
        );

      if (isContinuous) {
        // Merge to current top-level block
        current.end_dt = next.end_dt;
        current.days += next.days;
        current.brands.push({
          brand: next.brand,
          start_dt: next.start_dt,
          end_dt: next.end_dt,
          days: next.days
        });
      } else {
        // Finalize current block (merge brands within it)
        current.brands = mergeBrandSegments(current.brands);
        mergedPrograms.push(current);

        // Reset current
        current = {
          ...next,
          brands: [
            {
              brand: next.brand,
              start_dt: next.start_dt,
              end_dt: next.end_dt,
              days: next.days
            }
          ]
        };
      }
    }

    // Push the final merged group
    current.brands = mergeBrandSegments(current.brands);
    mergedPrograms.push(current);

    return mergedPrograms;
  }

  // Dispense Data for Header
  getDispenseData() {
    this.selctedLegendData = []
    this.currentLegendSelections = []
    this.totalLegendData = []
    const obj = {
      api_key: 100106,
      report_typ: "D",
      client_key: this.clientKey || "",
      patient_id: this.patientId
    }

    if(Object.keys(this.filterServiceOld.baseQuery).length>0) {
      Object.assign(obj, this.filterServiceOld.baseQuery);
    }

    this.reqSubcription.push(this.filterService.executeDynamicPatientQuery(this.widgetEndpoint, obj).subscribe((resp: any) => {
      
      if (resp) {
        this.headerChartData.dispense = resp
        this.headerChartData.dispense.forEach((i: any) => {
          if (i.type) {
            this.totalLegendData.push(i.type)
          }
        })
        this.unique = Array.from(new Set(this.totalLegendData.map((item: any) => item)))
        this.totalLegendData = this.unique
        let data = this.totalLegendData.map((i: any) => {
          if (i == 'Nurse Call') {
            return i.replace('Nurse Call', 'nursecall')
          } else return i.toLowerCase()
        })
        const uniqueArr = data.filter((value: any, index: any, self: any) => {
          return self.indexOf(value) === index;
        });
        this.selctedLegendData = uniqueArr
        this.totalLegendData = uniqueArr
        
        if(this.ganttChartData.length>0) {
          setTimeout(() => {this.plotChart();}, 100);
        }
      }
    }))
  }

  // KPI data for Header
  getkpiData() {
    const obj = {
      api_key: 100107,
      report_typ: "D",
      client_key: this.clientKey || "",
      patient_id: this.patientId
    }

    if(Object.keys(this.filterServiceOld.baseQuery).length>0) {
      Object.assign(obj, this.filterServiceOld.baseQuery);
    }

    this.reqSubcription.push(this.filterService.executeDynamicPatientQuery(this.widgetEndpoint, obj).subscribe((resp: any) => {
      if (resp.length>0) {
        this.headerChartData.kpi = resp;
      }

      // setTimeout(() => {this.plotChart();}, 100);
      
    }))
  }

  // Refill Data for Chart
  getRefillData() {
    const obj = {
      api_key: 100112,
      report_typ: "D",
      client_key: this.clientKey || "",
      patient_id: this.patientId  //10000976
    }

    if(Object.keys(this.filterServiceOld.baseQuery).length>0) {
      Object.assign(obj, this.filterServiceOld.baseQuery);
    }

    this.reqSubcription.push(this.filterService.executeDynamicPatientQuery(this.widgetEndpoint, obj).subscribe((resp: any) => {
      if (resp) {
        this.refillData = resp
        // this.plotChart();
        if(this.ganttChartData.length>0) {
          setTimeout(() => {this.plotChart();}, 100);
        }
      }
    }))
  }


  ngOnDestroy() {
    this.embeddedPlotContext = null;
    this.activeTreeToggle = null;
    this.reqSubcription.forEach((res: any) => res.unsubscribe())
    this.ganttChartData = '';
    this.headerChartData = ''
    this.transactionName = '';
    this.clientKey = '';
    this.moduleKey = '';
    this.dashboardKey = '';
    this.patientId = '';
    this.transactionData = '';
    this.refillData = '';
    this.radarChartData = '';
    this.pendingInformation = [];
    this.pointerTooltipData = '';
    this.currentLegendSelections = []
    this.selctedLegendData = []
    this.totalLegendData = []
    this.dataService.expandCollapseChart.next(false);
    this.dataService.radarData.next('')

  }
}